import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import { 
  Table, Input, Row, Col, Button, Card, Tag, Space, 
  Typography, Grid, Drawer, Popconfirm, message, Statistic, Badge, 
  Tooltip, Avatar, Divider, Select, DatePicker, Switch, Tabs, Collapse,
  Table as AntTable, Steps, Alert, Checkbox, Menu, Dropdown, Modal, Popover, List, Segmented, Upload, Progress, Empty
} from "antd";
import { 
  DownloadOutlined, FilterOutlined, ReloadOutlined, SearchOutlined,
  EditOutlined, DeleteOutlined, EyeOutlined, CalendarOutlined,
  TruckOutlined, BoxPlotOutlined, UserOutlined, CheckCircleOutlined,
  ClockCircleOutlined, WarningOutlined, AppstoreOutlined,
  ShoppingOutlined, FileExcelOutlined, SettingOutlined, FileImageOutlined,
  CloseOutlined, InfoCircleOutlined, DollarOutlined, LinkOutlined, BarcodeOutlined, TableOutlined, SaveOutlined,
  GlobalOutlined, MailOutlined, PhoneOutlined, SortAscendingOutlined, CopyOutlined, UploadOutlined,
  WeightIcon, EnvironmentOutlined, FileTextOutlined, InboxOutlined, DownOutlined, SyncOutlined,
  QuestionCircleOutlined

} from "@ant-design/icons";
import { Resizable } from "react-resizable";
import { useLocation } from "react-router-dom";
import moment from "moment";
import Cookies from "js-cookie";
import { Calendar } from "primereact/calendar";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import * as XLSX from "xlsx";
import axios from "axios";
import "./OrdersPage.css";
import config from "../config";
const { Search } = Input;
const { Option } = Select;
const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { useBreakpoint } = Grid;
const { TabPane } = Tabs;

const HOLD_REASONS = [
  "Customer Request",
  "Address Verification Pending",
  "Payment Confirmation",
  "Stock Unavailable",
  "Item Unavailable",
];

const COURIER_OPTIONS = [
  'DTDC Shipping',
  'Amazon Delivery',
  'Shiprocket DTDC',
  'Shiprocket Amazon',
  'Shiprocket Delivery Surface',
  'Standard Delivery',
  'Internal Transfer'
];

const PACKING_STATUS_NOT_AVAILABLE = '__PACKING_STATUS_NOT_AVAILABLE__';
const DEFAULT_ORDER_COLUMNS = ['sequence', 'order_id', 'date_time', 'customer', 'customer_tags', 'courier', 'awb', 'pickup', 'packing', 'packed_by', 'warehouse', 'box_type', 'actions'];

const isEmptyPackingStatus = (status) =>
  status === null ||
  status === undefined ||
  ['', 'n/a'].includes(String(status).trim().toLowerCase());

const ResizableTitle = (props) => {
  const { onResize, width, ...restProps } = props;
  if (!width) return <th {...restProps} />;
  
  return (
    <Resizable
      width={width}
      height={0}
      handle={
        <span
          className="react-resizable-handle"
          onClick={(e) => e.stopPropagation()}
        />
      }
      onResize={onResize}
      draggableOpts={{ enableUserSelectHack: false }}
    >
      <th {...restProps} />
    </Resizable>
  );
};

const FilterSection = ({ icon, title, children }) => (
  <div className="orders-filter-section">
    <div className="orders-filter-section-title">
      {icon && <span className="orders-filter-section-icon">{icon}</span>}
      <span>{title}</span>
    </div>
    <div className="orders-filter-section-body">{children}</div>
  </div>
);

const formatFilterDate = (value) => (value ? moment(value).format('DD MMM YY') : '');

const getPackingSessionHistory = (order) => {
  const history = Array.isArray(order?.packingSessionHistory) ? [...order.packingSessionHistory] : [];
  if (!history.length && order?.packingSessionId) {
    history.push({
      sessionId: order.packingSessionId,
      openedByName: order.packingSessionOpenedByName,
      openedAt: order.packingSessionOpenedAt,
      closedByName: order.packingSessionClosedByName,
      closedAt: order.packingSessionClosedAt,
      status: order.packingSessionOpen ? 'OPEN' : order.packed_status === 'Completed' ? 'COMPLETED' : 'CLOSED',
    });
  }
  return history.sort((a, b) => new Date(b.openedAt || 0) - new Date(a.openedAt || 0));
};

const OrdersPage = ({ orders = [], columnWidths = {}, handleResize, onRefresh, isRefreshing = false, darkTheme }) => {
  const screens = useBreakpoint();
  const location = useLocation();
  // States
  const [localOrders, setLocalOrders] = useState(orders);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [packedDateRange, setPackedDateRange] = useState(null);
  const [selectedCouriers, setSelectedCouriers] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [packingStatusFilter, setPackingStatusFilter] = useState([]);
  const [shipmentStatusFilter, setShipmentStatusFilter] = useState([]);
  const [sortConfig, setSortConfig] = useState({ field: 'packingscheduledat', order: 'descend' });
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [editFields, setEditFields] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [pageSize, setPageSize] = useState(15);
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [orderDetailsVisible, setOrderDetailsVisible] = useState(false);
  const [orderDetailsTab, setOrderDetailsTab] = useState('overview');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [packingMovements, setPackingMovements] = useState([]);
  const [packingMovementsLoading, setPackingMovementsLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [bulkUpdateModalVisible, setBulkUpdateModalVisible] = useState(false);
  const [bulkStatus, setBulkStatus] = useState(null);
  const [bulkAwbModalVisible, setBulkAwbModalVisible] = useState(false);
  const [bulkAwbData, setBulkAwbData] = useState([]);
  const [isBulkAwbUpdating, setIsBulkAwbUpdating] = useState(false);
  const [bulkAwbProgress, setBulkAwbProgress] = useState(0);
  const [bulkHoldReason, setBulkHoldReason] = useState("");
  const [bulkStockIssues, setBulkStockIssues] = useState([]);
  const [awbStatusFilter, setAwbStatusFilter] = useState(null);
  // Inline Editing State
  const [holdReasonFilter, setHoldReasonFilter] = useState("");
  const [stockIssuesFilter, setStockIssuesFilter] = useState([]);
  const [editingCell, setEditingCell] = useState(null);
  const [inlineValue, setInlineValue] = useState("");
  const ordersSearchRef = useRef(null);

  // Past Order Search State
  const [pastOrderIdSearch, setPastOrderIdSearch] = useState("");
  const [pastOrderLoading, setPastOrderLoading] = useState(false);
  const [pastOrderResult, setPastOrderResult] = useState(null);

  const [isOtherSelected, setIsOtherSelected] = useState(false);
  const [isCourierOther, setIsCourierOther] = useState(false);
  const [isBulkOtherSelected, setIsBulkOtherSelected] = useState(false);

  const startInlineEdit = (record, field, value) => {
    setEditingCell({ id: record.orderID, field });
    setInlineValue(value || "");
  };

  useEffect(() => {
    setLocalOrders(orders);
  }, [orders]);

  useEffect(() => {
    if (!orderDetailsVisible || !selectedOrder?.orderID) return undefined;
    let isCurrent = true;
    const currentOrderId = String(selectedOrder.orderID).trim();
    setPackingMovements([]);
    const loadPackingMovements = async () => {
      setPackingMovementsLoading(true);
      try {
        const token = Cookies.get('token') || localStorage.getItem('token');
        const response = await axios.get(`${config.apiBaseUrl}/api/packing-material-movements`, {
          params: { orderId: currentOrderId },
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const orderMovements = (response.data?.movements || []).filter((movement) => {
          const movementOrderId = String(movement.orderId || '').trim();
          const referenceId = String(movement.referenceId || '').trim();
          const packingSource = ['PACKING', 'PACKING_CORRECTION'].includes(String(movement.source || '').toUpperCase());
          return movementOrderId === currentOrderId || (packingSource && referenceId === currentOrderId);
        });
        if (isCurrent) setPackingMovements(orderMovements);
      } catch (error) {
        if (isCurrent) setPackingMovements([]);
        console.error('Failed to load packing material history for order:', error);
      } finally {
        if (isCurrent) setPackingMovementsLoading(false);
      }
    };
    loadPackingMovements();
    return () => { isCurrent = false; };
  }, [orderDetailsVisible, selectedOrder?.orderID]);

  // Handle navigation from Dashboard
  useEffect(() => {
    if (location.state?.filterByStockIssue) {
      setStockIssuesFilter([location.state.filterByStockIssue]);
      if (location.state.dateRange) {
        setDateRange([
          moment(location.state.dateRange[0]).startOf('day').toDate(),
          moment(location.state.dateRange[1]).endOf('day').toDate(),
        ]);
      }
    }
  }, [location.state]);

  const downloadAttachment = (attachment) => {
    try {
      const linkSource = `data:${attachment.mimetype};base64,${attachment.datas}`;
      const downloadLink = document.createElement("a");
      const fileName = attachment.name;

      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
      
      message.success(`Downloading ${fileName}`);
    } catch (err) {
      console.error("Download error:", err);
      message.error("Failed to download attachment");
    }
  };

  const saveInlineEdit = async () => {
    if (!editingCell) return;
    const { id, field } = editingCell;
    const cleanOrderID = id.split('#')[0];

    try {
      // Optimistic update
      setLocalOrders(prevOrders => prevOrders.map(order => {
        if (order.orderID === id) {
          const updatedShipments = [...(order.shipments || [])];
          if (updatedShipments.length > 0) {
             updatedShipments[0] = { ...updatedShipments[0], [field]: inlineValue };
          } else if (field === 'awb_code') {
             updatedShipments.push({ [field]: inlineValue, courier_name: '', status: '' });
          }
          return { ...order, shipments: updatedShipments };
        }
        return order;
      }));
      setEditingCell(null);

      await axios.patch(`${config.apiBaseUrl}/orders/update-fields/${cleanOrderID}`, {
        [field]: inlineValue
      });
      message.success('Updated successfully');
    } catch (err) {
      message.error('Failed to update');
    }
  };

  const downloadSampleAwbSheet = () => {
    const data = [
      { 'Order ID': 'ORD001', 'Courier Name': 'BlueDart', 'AWB Code': '1234567890' },
      { 'Order ID': 'ORD002', 'Courier Name': 'Delhivery', 'AWB Code': '0987654321' }
    ];
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");
    XLSX.writeFile(wb, "bulk_awb_update_template.xlsx");
  };

  const handleAwbFileUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet);
        
        if (jsonData.length === 0) {
          message.error("File is empty");
          return;
        }

        setBulkAwbData(jsonData);
        message.success(`Loaded ${jsonData.length} rows ready for update`);
      } catch (error) {
        message.error("Failed to parse file");
      }
    };
    reader.readAsArrayBuffer(file);
    return false;
  };

  const processBulkAwbUpdates = async () => {
    if (bulkAwbData.length === 0) return;
    
    setIsBulkAwbUpdating(true);
    setBulkAwbProgress(0);
    let successCount = 0;
    let failCount = 0;

    for (let i = 0; i < bulkAwbData.length; i++) {
      const row = bulkAwbData[i];
      const orderId = row['Order ID'];
      const courier = row['Courier Name'];
      const awb = row['AWB Code'];

      if (!orderId) continue;

      try {
        const cleanOrderID = orderId.toString().split('#')[0];
        await axios.patch(`${config.apiBaseUrl}/orders/update-fields/${cleanOrderID}`, {
          courier_name: courier,
          awb_code: awb
        });
        successCount++;
      } catch (error) {
        console.error(`Failed to update ${orderId}`, error);
        failCount++;
      }
      
      setBulkAwbProgress(Math.round(((i + 1) / bulkAwbData.length) * 100));
    }

    setIsBulkAwbUpdating(false);
    setBulkAwbModalVisible(false);
    setBulkAwbData([]);
    setBulkAwbProgress(0);
    
    if (successCount > 0) {
      message.success(`Successfully updated ${successCount} orders`);
      if (onRefresh) onRefresh();
    }
    if (failCount > 0) {
      message.warning(`Failed to update ${failCount} orders`);
    }
  };

  // New Features State
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('ordersPage_visibleColumns');
      const parsed = saved ? JSON.parse(saved) : [];
      const supported = Array.isArray(parsed) ? parsed.filter(key => DEFAULT_ORDER_COLUMNS.includes(key)) : [];
      const normalized = supported.length
        ? (supported.includes('customer_tags') ? supported : [...supported, 'customer_tags'])
        : DEFAULT_ORDER_COLUMNS;
      return normalized.includes('box_type') ? normalized : [...normalized, 'box_type'];
    } catch {
      return DEFAULT_ORDER_COLUMNS;
    }
  });

  const [savedViews, setSavedViews] = useState(() => {
    try {
      const saved = localStorage.getItem('ordersPage_savedViews');
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [viewName, setViewName] = useState("");
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  
  useEffect(() => {
    localStorage.setItem('ordersPage_visibleColumns', JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    localStorage.setItem('ordersPage_savedViews', JSON.stringify(savedViews));
  }, [savedViews]);

  const dashboardStats = useMemo(() => {
    const today = moment();
    const stats = {
      total: localOrders.length,
      completed: 0,
      pending: 0,
      hold: 0,
      cancelled: 0,
      overridden: 0,
      today: 0,
      overallNotCompleted: 0,
      missingAwbToday: 0,
      revenue: 0
    };

    // Preserve the existing calculations while traversing the 30-day array once.
    localOrders.forEach(order => {
      const status = order.packed_status;
      const isToday = order.createdAt &&
        moment(order.createdAt).isValid() &&
        moment(order.createdAt).isSame(today, 'day');

      if (status === "Completed") stats.completed += 1;
      if (status === "Hold") stats.hold += 1;
      if (status === "Cancelled") stats.cancelled += 1;
      if (status === "Overridden") stats.overridden += 1;
      if (!status || status === "Not Completed" || status === "Pending") {
        stats.overallNotCompleted += 1;
      }
      if (isToday) {
        stats.today += 1;
        if (!order.shipments?.[0]?.awb_code || order.shipments[0].awb_code.toString().trim() === '') {
          stats.missingAwbToday += 1;
        }
        if (!status || status === "Not Completed") stats.pending += 1;
      }

      order.products?.forEach(product => {
        const price = parseFloat(product.price) || 0;
        const quantity = parseInt(product.quantity) || 1;
        stats.revenue += price * quantity;
      });
    });

    return stats;
  }, [localOrders]);

  const filteredOrders = useMemo(() => {
    let filtered = [...localOrders];

    // Search filter
    if (searchText) {
      const searchTerms = searchText.split(/[\n,]+/).map(t => t.trim()).filter(t => t);

      filtered = filtered.filter(order => {
        return searchTerms.some(rawTerm => {
          const term = rawTerm.toLowerCase();
          // Order number (case-insensitive)
          const orderIdMatch = order.orderID?.toLowerCase().includes(term);
          // Customer number (mobile, allow partial match)
          const customerMobileMatch = order.customer?.mobile?.toString().includes(rawTerm);
          // Customer name/email
          const customerNameMatch = order.customer?.name?.toLowerCase().includes(term);
          const customerEmailMatch = order.customer?.email?.toLowerCase().includes(term);
          // AWB/courier
          const awbMatch = order.shipments?.[0]?.awb_code?.toLowerCase().includes(term);
          const courierMatch = order.shipments?.[0]?.courier_name?.toLowerCase().includes(term);
          return orderIdMatch || customerMobileMatch || customerNameMatch || customerEmailMatch || awbMatch || courierMatch;
        });
      });
    }

    // Date range filter - Using packingscheduledat (packing scheduled date)
    if (dateRange && dateRange[0]) {
      const startDate = moment(dateRange[0]).startOf("day");
      // If end date is not selected, use start date (single day filter)
      const endDate = dateRange[1] ? moment(dateRange[1]).endOf("day") : moment(dateRange[0]).endOf("day");
      
      filtered = filtered.filter(order => {
        if (!order.createdAt) return false;
        const packingDate = moment(order.createdAt);
        return packingDate.isBetween(startDate, endDate, null, "[]");
      });
    }

    // Packed Date range filter
    if (packedDateRange && packedDateRange[0]) {
      const startDate = moment(packedDateRange[0]).startOf("day");
      const endDate = packedDateRange[1] ? moment(packedDateRange[1]).endOf("day") : moment(packedDateRange[0]).endOf("day");
      
      filtered = filtered.filter(order => {
        if (!order.packed_date) return false;
        const pDate = moment(order.packed_date);
        return pDate.isBetween(startDate, endDate, null, "[]");
      });
    }

    // AWB Status Filter
    if (awbStatusFilter) {
      filtered = filtered.filter(order => {
        const awb = order.shipments?.[0]?.awb_code;
        const hasAwb = awb && awb.toString().trim() !== '';
        if (awbStatusFilter === 'With AWB') return hasAwb;
        if (awbStatusFilter === 'Without AWB') return !hasAwb;
        return true;
      });
    }

    // Courier filter
    if (selectedCouriers.length > 0) {
      filtered = filtered.filter(order => 
        selectedCouriers.includes(order.shipments?.[0]?.courier_name)
      );
    }

    // Product filter
    if (selectedProducts.length > 0) {
      filtered = filtered.filter(order =>
        order.products?.some(product => 
          selectedProducts.includes(product.name)
        )
      );
    }

    // Packing status filter (multi-select)
    if (packingStatusFilter.length > 0) {
      filtered = filtered.filter(order => packingStatusFilter.some(status =>
        status === PACKING_STATUS_NOT_AVAILABLE
          ? isEmptyPackingStatus(order.packed_status)
          : status === order.packed_status
      ));
    }

    // Shipment status filter (multi-select)
    if (shipmentStatusFilter.length > 0) {
      filtered = filtered.filter(order => 
        shipmentStatusFilter.includes(order.shipments?.[0]?.status)
      );
    }

    // Hold Reason filter
    if (holdReasonFilter.trim()) {
      const term = holdReasonFilter.trim().toLowerCase();
      filtered = filtered.filter(order => 
        order.hold_reason?.toLowerCase().includes(term)
      );
    }

    // Stock Issues filter
    if (stockIssuesFilter.length > 0) {
      filtered = filtered.filter(order =>
        order.stock_issues?.some(issue => stockIssuesFilter.includes(issue))
      );
    }

    // Sorting
    if (sortConfig.field && sortConfig.order) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.field];
        let bValue = b[sortConfig.field];
        
        if (sortConfig.field.includes('.')) {
          const keys = sortConfig.field.split('.');
          aValue = keys.reduce((obj, key) => obj?.[key], a);
          bValue = keys.reduce((obj, key) => obj?.[key], b);
        }
        
        if (aValue === undefined || aValue === null) aValue = '';
        if (bValue === undefined || bValue === null) bValue = '';
        
        if (sortConfig.order === 'ascend') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    }
    return filtered;
  }, [localOrders, searchText, dateRange, packedDateRange, selectedCouriers, selectedProducts, packingStatusFilter, shipmentStatusFilter, sortConfig, awbStatusFilter, holdReasonFilter, stockIssuesFilter]);

  // Merge past order result at the top of the table
  const ordersWithPastOrder = useMemo(() => {
    if (!pastOrderResult) return filteredOrders;
    // Avoid duplicates
    const exists = filteredOrders.some(o => o.orderID === pastOrderResult.orderID);
    if (exists) return filteredOrders;
    return [pastOrderResult, ...filteredOrders];
  }, [filteredOrders, pastOrderResult]);

  const handleDateChange = (e) => {
    setDateRange(e.value);
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      order: prev.field === field && prev.order === 'ascend' ? 'descend' : 'ascend'
    }));
  };

  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    message.success('Copied to clipboard');
  };

  const handleOpenOdoo = (order) => {
    const orderReference = String(order?.orderID || '').split('#')[0].trim();
    const saleOrderNumber = orderReference.replace(/^SO?/i, '');

    if (!saleOrderNumber) {
      message.warning('Odoo sale order number is not available');
      return;
    }

    window.open(
      `https://www.thegoodhealth.co.in/odoo/sales/${encodeURIComponent(saleOrderNumber)}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedRowKeys.length === 0) return;
    
    if (bulkStatus === 'Hold' && !bulkHoldReason?.trim()) {
      message.error('Please provide a reason for putting orders on hold');
      return;
    }

    try {
      message.loading({ content: 'Updating orders...', key: 'bulkUpdate' });
      
      // Execute updates in parallel
      const updatePromises = selectedRowKeys.map(orderId => {
        const cleanOrderID = orderId.split('#')[0];
        return axios.patch(`${config.apiBaseUrl}/orders/update-fields/${cleanOrderID}`, {
          packed_status: bulkStatus,
          hold_reason: bulkHoldReason,
          stock_issues: bulkStockIssues
        });
      });
      
      await Promise.all(updatePromises);
      
      message.success({ content: `Successfully updated ${selectedRowKeys.length} orders`, key: 'bulkUpdate' });
      setBulkUpdateModalVisible(false);
      setBulkStatus(null);
      setBulkHoldReason("");
      setBulkStockIssues([]);
      setSelectedRowKeys([]);
      if (onRefresh) onRefresh();
    } catch (error) {
      message.error({ content: 'Failed to update some orders', key: 'bulkUpdate' });
    }
  };

  const handleSaveView = () => {
    if (!viewName.trim()) {
      message.error("Please enter a name for the view");
      return;
    }
    const newView = {
      id: Date.now(),
      name: viewName,
      filters: {
        searchText,
        dateRange,
        packedDateRange,
        selectedCouriers,
        selectedProducts,
        packingStatusFilter,
        shipmentStatusFilter,
        sortConfig
      }
    };
    setSavedViews([...savedViews, newView]);
    setViewName("");
    setSaveViewOpen(false);
    message.success("View saved successfully");
  };

  const handleApplyView = (view) => {
    const { filters } = view;
    setSearchText(filters.searchText || "");
    
    if (filters.dateRange && filters.dateRange[0]) {
      setDateRange([new Date(filters.dateRange[0]), new Date(filters.dateRange[1])]);
    } else {
      setDateRange(null);
    }
    
    if (filters.packedDateRange && filters.packedDateRange[0]) {
      setPackedDateRange([new Date(filters.packedDateRange[0]), new Date(filters.packedDateRange[1])]);
    } else {
      setPackedDateRange(null);
    }

    setSelectedCouriers(filters.selectedCouriers || []);
    setSelectedProducts(filters.selectedProducts || []);
    setPackingStatusFilter(filters.packingStatusFilter || []);
    setShipmentStatusFilter(filters.shipmentStatusFilter || []);
    setSortConfig(filters.sortConfig || { field: 'packingscheduledat', order: 'descend' });
    message.success(`Applied view: ${view.name}`);
  };

  const handleDeleteView = (id) => {
    setSavedViews(savedViews.filter(v => v.id !== id));
    message.success("View deleted");
  };

  const handleEditOrder = (order) => {
    if (order.packed_status === 'Completed') {
      message.warning('Cannot edit a completed order');
      return;
    }
    const isPredefined = HOLD_REASONS.includes(order.hold_reason);
    setIsOtherSelected(!!order.hold_reason && !isPredefined);
    setEditingOrder(order);
    setEditFields({
      courier_name: order.shipments?.[0]?.courier_name || '',
      awb_code: order.shipments?.[0]?.awb_code || '',
      packed_status: order.packed_status || '',
      hold_reason: order.hold_reason || '',
      stock_issues: order.stock_issues || [],
    });
    // detect if courier is one of predefined options
    const existingCourier = (order.shipments?.[0]?.courier_name || '').toString().trim();
    const isKnown = existingCourier && COURIER_OPTIONS.some(c => c.toLowerCase() === existingCourier.toLowerCase());
    setIsCourierOther(!isKnown && !!existingCourier);
    setEditModalVisible(true);
  };

  useEffect(() => {
    const isEditableTarget = (target) => Boolean(target?.closest(
      'input, textarea, select, button, [contenteditable="true"], .ant-select, .ant-picker, [role="dialog"]'
    ));

    const handleOrdersShortcut = (event) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;

      if (event.key === 'Escape') {
        if (orderDetailsVisible || filterDrawerVisible || editModalVisible || bulkUpdateModalVisible || bulkAwbModalVisible) {
          setOrderDetailsVisible(false);
          setFilterDrawerVisible(false);
          setEditModalVisible(false);
          setBulkUpdateModalVisible(false);
          setBulkAwbModalVisible(false);
          return;
        }
        return;
      }

      if (isEditableTarget(event.target)) return;

      if (event.key === '/') {
        event.preventDefault();
        ordersSearchRef.current?.focus();
        return;
      }

      if (event.key.toLowerCase() === 'r') {
        event.preventDefault();
        onRefresh?.();
        return;
      }

      if (selectedRowKeys.length !== 1) return;

      const selectedOrderForShortcut = localOrders.find(order => order.orderID === selectedRowKeys[0]);
      if (!selectedOrderForShortcut) return;

      if (event.key.toLowerCase() === 'v') {
        event.preventDefault();
        setSelectedOrder(selectedOrderForShortcut);
        setOrderDetailsVisible(true);
      }

      if (event.key.toLowerCase() === 'e') {
        event.preventDefault();
        handleEditOrder(selectedOrderForShortcut);
      }
    };

    window.addEventListener('keydown', handleOrdersShortcut);
    return () => window.removeEventListener('keydown', handleOrdersShortcut);
  }, [bulkAwbModalVisible, bulkUpdateModalVisible, editModalVisible, filterDrawerVisible, localOrders, onRefresh, orderDetailsVisible, selectedRowKeys]);

  const handleSaveEdit = async () => {
    if (!editingOrder) return;

    if (editFields.packed_status === 'Hold' && !editFields.hold_reason?.trim()) {
      message.error('Please provide a reason for putting the order on hold');
      return;
    }

    try {
      // Remove hash and anything after it from orderID if present
      const cleanOrderID = editingOrder.orderID.split('#')[0];
      
      await axios.patch(`${config.apiBaseUrl}/orders/update-fields/${cleanOrderID}`, {
        courier_name: editFields.courier_name,
        awb_code: editFields.awb_code,
        packed_status: editFields.packed_status,
        hold_reason: editFields.hold_reason,
        stock_issues: editFields.stock_issues
      });
      
      message.success('Order updated successfully');
      
      // Optimistic update locally
      setLocalOrders(prevOrders => prevOrders.map(order => {
        if (order.orderID === editingOrder.orderID) {
          const updatedShipments = [...(order.shipments || [])];
          if (updatedShipments.length > 0) {
            updatedShipments[0] = {
              ...updatedShipments[0],
              courier_name: editFields.courier_name,
              awb_code: editFields.awb_code
            };
          }
          return {
            ...order,
            packed_status: editFields.packed_status,
            hold_reason: editFields.hold_reason,
            stock_issues: editFields.stock_issues,
            shipments: updatedShipments
          };
        }
        return order;
      }));

      setEditModalVisible(false);
      setEditingOrder(null);
      
      // Trigger refresh from parent
      if (onRefresh) {
        onRefresh();
      }
    } catch (err) {
      message.error('Failed to update order: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      const cleanOrderID = orderId.split('#')[0];
      await axios.delete(`/api/orders/${cleanOrderID}`);
      message.success('Order deleted successfully');
      setLocalOrders(prev => prev.filter(o => o.orderID !== orderId));
    } catch (error) {
      message.error('Failed to delete order');
    }
  };

  const getGrossWeight = (order) => {
    // Calculate gross weight from products only, ignoring final_package_weight
    if (!order.products || order.products.length === 0) {
      return 0;
    }
    
    let totalWeight = 0;
    order.products.forEach((product) => {
      let weight = 0;
      
      const directWeight = 
        product.weight || 
        product.Weight || 
        product.product_weight || 
        product.item_weight || 
        product.grams ||
        product.item_grams;
      
      if (directWeight && directWeight !== 0 && directWeight !== '0') {
        weight = typeof directWeight === 'string' ? parseFloat(directWeight) : Number(directWeight);
        weight = isNaN(weight) || weight < 0 ? 0 : weight;
      }
      
      if (weight === 0 && product.sku) {
        const skuMatch = product.sku.match(/(\d+(?:\.\d+)?)\s*(g|kg|ml|l|oz|lb)/i);
        if (skuMatch) {
          let value = parseFloat(skuMatch[1]);
          const unit = skuMatch[2].toLowerCase();
          
          switch(unit) {
            case 'g':
              weight = value / 1000;
              break;
            case 'kg':
              weight = value;
              break;
            case 'ml':
              weight = value / 1000;
              break;
            case 'l':
              weight = value;
              break;
            case 'oz':
              weight = value * 0.0283495;
              break;
            case 'lb':
              weight = value * 0.453592;
              break;
            default:
              weight = 0;
          }
        }
      }
      
      const qty = parseInt(product.quantity) || 1;
      totalWeight += (weight * qty);
    });
    
    return isNaN(totalWeight) ? 0 : totalWeight;
  };

  const handleExportExcel = (ordersToExport = filteredOrders) => {
    if (!ordersToExport || ordersToExport.length === 0) return;

    const data = ordersToExport.map(order => {
      const grossWeight = getGrossWeight(order);
      const finalWeight = order.final_package_weight ? parseFloat(order.final_package_weight) : null;
      const boxType = order.box_type ? `Box Type ${order.box_type}` : '';
      
      return {
        'Order ID': order.orderID,
        'Customer': order.customer?.name,
        'Mobile': order.customer?.mobile,
        'Email': order.customer?.email,
        'Order Date': moment(order.order_date).format('DD/MM/YYYY HH:mm'),
        'Courier': order.shipments?.[0]?.courier_name,
        'AWB': order.shipments?.[0]?.awb_code,
        'Status': order.shipments?.[0]?.status,
        'Packing Status': order.packed_status,
        'Total Products': order.products?.length || 0,
        'Gross Weight (kg)': grossWeight.toFixed(2),
        'Final Weight (kg)': finalWeight ? finalWeight.toFixed(2) : '',
        'Box Type': boxType,
        'Warehouse': order.warehouse_out,
        'Hold Reason': order.hold_reason
      };
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orders');
    XLSX.writeFile(wb, `orders_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
  };

  const getUniqueProductsFromSelected = () => {
    const selectedOrders = localOrders.filter(o => selectedRowKeys.includes(o.orderID));
    const products = new Set();
    selectedOrders.forEach(o => {
      o.products?.forEach(p => products.add(p.name));
    });
    return Array.from(products).sort();
  };

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'DELIVERED': { color: 'success', icon: <CheckCircleOutlined />, label: 'Delivered' },
      'IN TRANSIT': { color: 'processing', icon: <TruckOutlined />, label: 'In Transit' },
      'OUT FOR DELIVERY': { color: 'cyan', icon: <TruckOutlined />, label: 'Out for Delivery' },
      'PICKED UP': { color: 'geekblue', icon: <BoxPlotOutlined />, label: 'Picked Up' },
      'RTO': { color: 'error', icon: <WarningOutlined />, label: 'RTO' },
      'Pending': { color: 'warning', icon: <ClockCircleOutlined />, label: 'Pending' },
      'OUT FOR PICKUP': { color: 'purple', icon: <TruckOutlined />, label: 'Out for Pickup' }
    };
    
    const config = statusConfig[status] || { color: 'default', icon: null, label: status || 'N/A' };
    return (
      <Tag color={config.color} style={{ margin: 0, borderRadius: '4px', border: '1px solid' }}>
        <Space size={4}>
          {config.icon}
          {config.label}
        </Space>
      </Tag>
    );
  };

  const getPackingStatus = (status) => {
    const colors = {
      'Completed': 'success',
      'Hold': 'warning',
      'Overridden': 'purple',
      'Not Completed': 'error',
      'Pending': 'default'
    };
    
    const icons = {
      'Completed': <CheckCircleOutlined />,
      'Hold': <WarningOutlined />,
      'Overridden': <SettingOutlined />,
      'Pending': <ClockCircleOutlined />,
      'Not Completed': <WarningOutlined />
    };
    
    const displayStatus = status || 'N/A';
    return (
      <Tag 
        color={colors[status] || 'default'}
        icon={icons[status]}
        style={{ 
          borderRadius: '4px', 
          fontWeight: 500,
          fontSize: '12px',
          padding: '2px 8px',
          margin: 0,
          border: '1px solid'
        }}
      >
        {displayStatus}
      </Tag>
    );
  };

  const getCourierMeta = (courier) => {
    const value = String(courier || '').trim().toLowerCase();
    if (!value) return { label: 'N/A', image: null };
    if (value.includes('amazon')) return { label: 'Amazon', image: '/Amazon.ico' };
    if (value.includes('delhivery') || value.includes('delivery surface')) return { label: 'Delhivery Surface', image: '/Delivery%20surface.png' };
    if (value.includes('dtdc')) return { label: 'DTDC', image: '/DTDC%20logo.webp' };
    return { label: 'N/A', image: null };
  };

  const getDrawerStatusConfig = (status) => {
    const normalized = String(status || '').trim();
    if (normalized.toLowerCase() === 'completed') return { label: 'Completed', color: 'success', icon: <CheckCircleOutlined /> };
    if (normalized.toLowerCase() === 'hold') return { label: 'On Hold', color: 'warning', icon: <WarningOutlined /> };
    if (normalized.toLowerCase() === 'cancelled') return { label: 'Cancelled', color: 'error', icon: <CloseOutlined /> };
    return { label: 'Not Completed', color: 'default', icon: <ClockCircleOutlined /> };
  };

  const getPackingProgressStages = (order) => {
    const shipmentStatus = String(order?.shipments?.[0]?.status || '').toUpperCase();
    const hasPackedDate = !!order?.packed_date || order?.packed_status === 'Completed';
    const hasShipped = !!order?.warehouse_out_date || order?.warehouse_out === 'Yes' || ['IN TRANSIT', 'OUT FOR DELIVERY', 'PICKED UP', 'OUT FOR PICKUP', 'DELIVERED', 'SHIPPED'].includes(shipmentStatus);
    const hasDelivered = shipmentStatus === 'DELIVERED';

    return [
      {
        key: 'placed',
        label: 'Placed',
        time: order?.order_date ? moment(order.order_date).format('DD MMM, hh:mm A') : null,
        done: true,
      },
      {
        key: 'packed',
        label: 'Packed',
        time: order?.packed_date ? moment(order.packed_date).format('DD MMM, hh:mm A') : null,
        done: hasPackedDate,
      },
      {
        key: 'shipped',
        label: 'Shipped',
        time: order?.warehouse_out_date ? moment(order.warehouse_out_date).format('DD MMM, hh:mm A') : null,
        done: hasShipped,
      },
      {
        key: 'delivered',
        label: 'Delivered',
        time: hasDelivered && order?.shipments?.[0]?.status ? moment(order.order_date).format('DD MMM, hh:mm A') : null,
        done: hasDelivered,
      },
    ];
  };

  const getProductThumbnail = (product) => (
    product?.imageUrl ||
    product?.image ||
    product?.product_image ||
    product?.image_url ||
    product?.thumbnail ||
    product?.productImage ||
    ''
  );

  const getCourierLabel = (courier) => getCourierMeta(courier).label;

  const getConsolidatedDelivery = (deliveries = []) => {
    const validDeliveries = (deliveries || []).filter((delivery) => {
      const isWHOut = delivery.name?.includes('WH/OUT');
      const isCustomerDelivery = delivery.destination === 'Partners/Customers';
      const hasProducts = Array.isArray(delivery.products) && delivery.products.length > 0;
      return (isWHOut || isCustomerDelivery) && hasProducts && !delivery.name?.includes('WH/IN');
    });

    if (!validDeliveries.length) return null;
    if (validDeliveries.length === 1) return validDeliveries[0];

    const productMap = new Map();
    validDeliveries.forEach((delivery) => {
      const deliveryDate = delivery.scheduled_date ? new Date(delivery.scheduled_date).getTime() : 0;
      (delivery.products || []).forEach((product) => {
        const key = product.product_id?.toString() || `${product.barcode || ''}_${product.reference || ''}`;
        const existing = productMap.get(key);
        if (!existing || deliveryDate > existing.scheduled_date) {
          productMap.set(key, { product: { ...product }, scheduled_date: deliveryDate, deliveryId: delivery.id });
        }
      });
    });

    const consolidatedProducts = Array.from(productMap.values()).map((value) => value.product);
    const latestDelivery = validDeliveries.reduce((latest, current) => {
      const latestTime = latest.scheduled_date ? new Date(latest.scheduled_date).getTime() : 0;
      const currentTime = current.scheduled_date ? new Date(current.scheduled_date).getTime() : 0;
      return currentTime > latestTime ? current : latest;
    }, validDeliveries[0]);

    const trackingCandidates = validDeliveries.filter(d => d.carrier_tracking_ref && d.carrier_tracking_ref.toString().trim() !== '');
    const latestTrackingDelivery = trackingCandidates.length
      ? trackingCandidates.reduce((best, current) => {
          const bestTime = best.scheduled_date ? new Date(best.scheduled_date).getTime() : 0;
          const currentTime = current.scheduled_date ? new Date(current.scheduled_date).getTime() : 0;
          return currentTime > bestTime ? current : best;
        }, trackingCandidates[0])
      : null;

    const carrierCandidates = validDeliveries.filter(d => d.carrier && d.carrier.toString().trim() !== '');
    const latestCarrierDelivery = carrierCandidates.length
      ? carrierCandidates.reduce((best, current) => {
          const bestTime = best.scheduled_date ? new Date(best.scheduled_date).getTime() : 0;
          const currentTime = current.scheduled_date ? new Date(current.scheduled_date).getTime() : 0;
          return currentTime > bestTime ? current : best;
        }, carrierCandidates[0])
      : null;

    return {
      id: latestDelivery.id,
      name: latestDelivery.name,
      destination: latestDelivery.destination,
      state: latestDelivery.state,
      carrier: latestCarrierDelivery?.carrier || latestDelivery.carrier || '',
      carrier_tracking_ref: latestTrackingDelivery?.carrier_tracking_ref || latestDelivery.carrier_tracking_ref || '',
      carrier_tracking_url: latestTrackingDelivery?.carrier_tracking_url || latestDelivery.carrier_tracking_url || '',
      products: consolidatedProducts,
      scheduled_date: latestDelivery.scheduled_date,
      attachments: latestTrackingDelivery?.attachments || latestDelivery.attachments || []
    };
  };

  const syncSingleOrder = async (order, showToast = true) => {
    const orderIdentifier = String(order?.orderID || '').split('#')[0].trim();
    if (!orderIdentifier) {
      throw new Error('Order number is missing');
    }

    const response = await axios.get(`${config.apiBaseUrl}/api/sale-orders/search/${encodeURIComponent(orderIdentifier)}`);
    const odooOrder = response?.data?.data;
    if (!odooOrder) {
      throw new Error('Order not found in Odoo');
    }

    const consolidatedDelivery = getConsolidatedDelivery(odooOrder.deliveries || []);
    const products = (consolidatedDelivery?.products || []).map((product) => ({
      id: product.product_id?.toString() || `TEMP-${Date.now()}-${Math.random()}`,
      updated_id: product.barcode || product.product_id || '',
      sku: product.reference || '',
      name: product.name || '',
      quantity: product.qty || 0,
      weight: 0,
      imageUrl: '',
      productLocation: 'Unknown',
      productCategory: 'Unknown'
    }));

    const payload = {
      orderID: odooOrder.name || orderIdentifier,
      order_date: odooOrder.date_order || new Date().toISOString(),
      customer: {
        name: odooOrder.customer?.name || '',
        mobile: odooOrder.customer?.phone || '',
        email: odooOrder.customer?.email || '',
        tags: odooOrder.customer?.tags || [],
        sale_order_count: odooOrder.customer?.sale_order_count || 0
      },
      shipments: consolidatedDelivery ? [{
        courier_name: consolidatedDelivery.carrier || '',
        awb_code: consolidatedDelivery.carrier_tracking_ref || '',
        status: 'PICKUP SCHEDULED',
        attachments: consolidatedDelivery.attachments || []
      }] : [],
      products,
      packed_status: 'Not Completed',
      packed_time: 'Unknown Time',
      packed_person_name: 'Unknown',
      warehouse_out: 'Unknown',
      warehouse_out_time: 'Unknown Time',
      final_package_weight: 0,
      box_type: null,
      packingscheduledat: new Date().toISOString()
    };

    await axios.post(`${config.apiBaseUrl}/api/orders`, payload);

    if (showToast) {
      message.success(`✅ ${payload.orderID} synced successfully`);
    }

    return payload;
  };

  const handleSyncSelectedOrders = async () => {
    const selectedOrders = localOrders.filter(order => selectedRowKeys.includes(order.orderID));
    const eligibleOrders = selectedOrders.filter(order => !['Completed', 'Cancelled'].includes(order.packed_status));

    if (!eligibleOrders.length) {
      message.warning('Completed and cancelled orders are excluded from resync.');
      return;
    }

    const limitedOrders = eligibleOrders.slice(0, 50);
    let successCount = 0;
    let failedCount = 0;

    for (const order of limitedOrders) {
      try {
        await syncSingleOrder(order, false);
        successCount += 1;
      } catch (error) {
        failedCount += 1;
        console.error('Sync selected order failed:', error);
      }
    }

    if (successCount > 0) {
      message.success(`✅ ${successCount} order${successCount > 1 ? 's were' : ' was'} synced successfully`);
    }
    if (failedCount > 0) {
      message.error(`❌ ${failedCount} order${failedCount > 1 ? 's' : ''} failed to sync`);
    }

    if (onRefresh) onRefresh();
  };

  const getPickupStatus = (record) => {
    const pickupStatus = String(record.shipments?.[0]?.pickup_status || record.shipments?.[0]?.status || '').toUpperCase();
    return pickupStatus === 'PICKUP SCHEDULED' ? 'Pickup Scheduled' : 'N/A';
  };

  const getOrderTotalWeight = (order) => {
    // First priority: use final_package_weight if available and > 0
    if (order.final_package_weight && parseFloat(order.final_package_weight) > 0) {
      return parseFloat(order.final_package_weight);
    }
    
    // Second priority: calculate from products
    if (!order.products || order.products.length === 0) {
      return order.final_package_weight ? parseFloat(order.final_package_weight) : 0;
    }
    
    let totalWeight = 0;
    order.products.forEach((product, idx) => {
      let weight = 0;
      
      // Try multiple weight field names first
      const directWeight = 
        product.weight || 
        product.Weight || 
        product.product_weight || 
        product.item_weight || 
        product.grams ||
        product.item_grams;
      
      if (directWeight && directWeight !== 0 && directWeight !== '0') {
        weight = typeof directWeight === 'string' ? parseFloat(directWeight) : Number(directWeight);
        weight = isNaN(weight) || weight < 0 ? 0 : weight;
      }
      
      // If no direct weight, try to extract from SKU (e.g., "100g.TMN" → 100g = 0.1kg)
      if (weight === 0 && product.sku) {
        // Match patterns like: 100g, 250g, 1kg, 500ml, etc.
        const skuMatch = product.sku.match(/(\d+(?:\.\d+)?)\s*(g|kg|ml|l|oz|lb)/i);
        if (skuMatch) {
          let value = parseFloat(skuMatch[1]);
          const unit = skuMatch[2].toLowerCase();
          
          // Convert to kg
          switch(unit) {
            case 'g':
              weight = value / 1000; // grams to kg
              break;
            case 'kg':
              weight = value;
              break;
            case 'ml':
              weight = value / 1000; // ml to kg (assuming ~1g per ml for water)
              break;
            case 'l':
              weight = value; // liters to kg (assuming ~1kg per liter)
              break;
            case 'oz':
              weight = value * 0.0283495; // ounces to kg
              break;
            case 'lb':
              weight = value * 0.453592; // pounds to kg
              break;
            default:
              weight = 0;
          }
        }
      }
      
      const qty = parseInt(product.quantity) || 1;
      totalWeight += (weight * qty);
      
      // Debug: Log extraction for first product
      if (idx === 0 && totalWeight > 0) {       
      }
    });
    
    return isNaN(totalWeight) ? 0 : totalWeight;
  };

  const getTotalOrderValue = (order) => {
    if (!order.products) return 0;
    return order.products.reduce((sum, product) => {
      const price = parseFloat(product.price) || 0;
      const quantity = parseInt(product.quantity) || 1;
      return sum + (price * quantity);
    }, 0);
  };

  const getTagColor = (tagName) => {
    if (!tagName) return 'rgb(65, 144, 250)';
    const t = tagName.toString().trim().toLowerCase();
    const map = {
      'vvip': '#c41d7f',
      'v vip': '#c41d7f',
      'vip': '#fa8c16',
      'loyal': '#52c41a',
      'repeat': '#2f54eb',
      'new': '#13c2c2',
      'other': 'rgb(65, 144, 250)'
    };
    return map[t] || 'rgb(65, 144, 250)';
  };

  const getCustomerInitial = (name) => {
    const normalizedName = String(name || '').trim();
    return normalizedName ? normalizedName.charAt(0).toUpperCase() : '?';
  };

  const getCustomerTags = (record) => {
    const sourceTags = record.customer?.tags || [];
    const customerTags = sourceTags
      .map(tag => (typeof tag === 'string' ? tag : tag?.name))
      .map(tag => String(tag || '').trim())
      .filter(Boolean);

    if (record.customer?.sale_order_count > 1 && !customerTags.some(tag => tag.toLowerCase() === 'repeat')) {
      customerTags.push('Repeat');
    }

    return Array.from(new Set(customerTags));
  };

  const allColumns = [
    {
      title: 'ORDER DETAILS',
      key: 'order',
      width: 80,
      fixed: 'left',
      render: (record) => (
        <div style={{ lineHeight: 1.4 }}>
          <Space>
            <Text strong style={{ fontSize: '13px', display: 'block' }}>{record.orderID}</Text>
            <Tooltip title="Copy Order ID">
              <CopyOutlined 
                style={{ fontSize: '10px', cursor: 'pointer', color: '#1890ff', opacity: 0.7 }} 
                onClick={(e) => { e.stopPropagation(); handleCopy(record.orderID); }}
              />
            </Tooltip>
          </Space>
          <Text type="secondary" style={{ fontSize: '11px', display: 'block' }}>
            {moment(record.order_date).format('DD MMM, hh:mm A')}
          </Text>
        </div>
      ),
    },
    {
      title: 'CUSTOMER INFO',
      key: 'customer',
      width: 100,
      render: (record) => (
        <div style={{ lineHeight: 1.5 }}>
          <Space direction="vertical" size={6} style={{ width: '100%' }}>
            <Space size={6} style={{ width: '100%', flexWrap: 'wrap' }}>
              <UserOutlined style={{ fontSize: '12px', color: '#8c8c8c', flexShrink: 0 }} />
              <Text strong style={{ fontSize: '12px' }}>{record.customer?.name || 'N/A'}</Text>
            </Space>
            <Space size={6} style={{ width: '100%', flexWrap: 'wrap' }}>
              <PhoneOutlined style={{ fontSize: '12px', color: '#8c8c8c', flexShrink: 0 }} />
              <Text style={{ fontSize: '12px' }}>{record.customer?.mobile || 'N/A'}</Text>
              {record.customer?.mobile && (
                <CopyOutlined 
                  style={{ fontSize: '11px', cursor: 'pointer', color: '#bfbfbf', flexShrink: 0 }} 
                  onClick={(e) => { e.stopPropagation(); handleCopy(record.customer.mobile); }}
                />
              )}
            </Space>
            <Space size={6} style={{ width: '100%', flexWrap: 'wrap' }}>
              <MailOutlined style={{ fontSize: '12px', color: '#8c8c8c', flexShrink: 0 }} />
              <Text style={{ fontSize: '11px' }} ellipsis>{record.customer?.email || 'N/A'}</Text>
            </Space>
          </Space>
        </div>
      ),
    },
    {
      title: 'CUSTOMER STATS',
      key: 'customer_stats',
      width: 100,
      render: (record) => {
        const odooTags = record.customer?.tags?.map(tag => 
          typeof tag === 'string' ? tag : tag.name
        ) || [];
        
        const allTags = [...odooTags];
        if (record.customer?.sale_order_count > 1 && !odooTags.some(t => t.toLowerCase() === 'repeat')) {
          allTags.push('Repeat');
        }
        
        const uniqueTags = Array.from(new Set(allTags));

        return (
          <div >
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Space align="center">
                <Text type="secondary" style={{ fontSize: '11px' }}>Sales:</Text>
                <Badge 
                  count={record.customer?.sale_order_count || 0} 
                  showZero 
                  color="rgb(65, 144, 250)"
                  title={`Total Sales: ${record.customer?.sale_order_count || 0}`}
                />
              </Space>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: 4 }}>
                {uniqueTags.map((tagName, idx) => (
                  <Tag
                    key={idx}
                    color={getTagColor(tagName)}
                    style={{ fontSize: '10px', margin: 0, borderRadius: '4px', padding: '2px 6px', border: '1px solid rgba(0,0,0,0.08)' }}
                  >
                    {tagName}
                  </Tag>
                ))}
              </div>
            </Space>
          </div>
        );
      },
    },
    {
      title: 'SHIPPING',
      key: 'shipping',
      width: 100,
      render: (record) => (
        <div style={{ lineHeight: 1.4 }}>
          <Space direction="vertical" size={2} style={{ width: '100%' }}>
            <Space size={4}>
              <TruckOutlined style={{ fontSize: '11px', color: '#1890ff' }} />
              <Text style={{ fontSize: '12px' }}>{record.shipments?.[0]?.courier_name || 'N/A'}</Text>
            </Space>
            {editingCell?.id === record.orderID && editingCell?.field === 'awb_code' ? (
               <Input
                 size="small"
                 value={inlineValue}
                 onChange={e => setInlineValue(e.target.value)}
                 onPressEnter={saveInlineEdit}
                 onBlur={saveInlineEdit}
                 autoFocus
                 style={{ width: '100%', fontSize: '11px', height: 24, marginTop: 2 }}
                 onClick={e => e.stopPropagation()}
               />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  AWB: {record.shipments?.[0]?.awb_code ? (
                    <a target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}>
                      {record.shipments[0].awb_code} <LinkOutlined style={{ fontSize: '10px' }}/>
                    </a>
                  ) : 'N/A'}
                </Text>
                <Space size={2}>
                  {record.shipments?.[0]?.awb_code && (
                    <CopyOutlined style={{ fontSize: '10px', cursor: 'pointer', color: '#999' }} onClick={(e) => { e.stopPropagation(); handleCopy(record.shipments[0].awb_code); }} />
                  )}
                  <EditOutlined 
                    style={{ fontSize: '10px', cursor: 'pointer', color: '#1890ff' }} 
                    onClick={(e) => { e.stopPropagation(); startInlineEdit(record, 'awb_code', record.shipments?.[0]?.awb_code); }} 
                  />
                </Space>
              </div>
            )}
            {record.shipments?.[0]?.attachments?.length > 0 && (
              <div style={{ marginTop: 4 }}>
                {record.shipments[0].attachments.map((att, idx) => (
                  <Button 
                    key={idx}
                    type="link" 
                    size="small" 
                    icon={<FileImageOutlined />}
                    onClick={(e) => { e.stopPropagation(); downloadAttachment(att); }}
                    style={{ padding: 0, height: 'auto', fontSize: '11px' }}
                  >
                    Download Label
                  </Button>
                ))}
              </div>
            )}
             <Text type="secondary" style={{ fontSize: '10px' }}>📅 Created: {moment(record.createdAt).format('DD MMM, hh:mm A')}</Text>
          </Space>
        </div>
      ),
    },
    {
      title: 'PACKING STATUS',
      key: 'status',
      width: 100,
      render: (record) => (
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          <div>{getStatusBadge(record.shipments?.[0]?.status)}</div>
          <div>{getPackingStatus(record.packed_status)}</div>
        </Space>
      ),
    },
    {
      title: 'PACKED BY',
      key: 'packed_by',
      width: 80,
      render: (record) => (
        <div style={{ lineHeight: 1.4 }}>
          {record.packed_person_name ? (
            <>
              <div style={{ fontWeight: 600, fontSize: '12px', marginBottom: 2 }}>
                <UserOutlined style={{ marginRight: 4, color: '#1890ff' }} />
                {record.packed_person_name}
              </div>
              <div style={{ fontSize: '11px', color: '#8c8c8c' }}>
                📅 {record.packed_date ? moment(record.packed_date).format('DD MMM, hh:mm A') : 'N/A'}
              </div>
            </>
          ) : (
            <Text type="secondary" style={{ fontSize: '12px' }}>-</Text>
          )}
        </div>
      ),
    },
   
    {
      title: 'WAREHOUSE',
      key: 'warehouse_status',
      width: 100,
      render: (record) => {
        const isOut = record.warehouse_out === 'Yes' || record.warehouse_out_date;
        return (
          <Tag 
            color={isOut ? '#52c41a' : 'default'} 
            style={{ 
              borderRadius: '12px', 
              fontWeight: 500,
              fontSize: '12px',
              padding: '2px 8px',
              margin: 0
            }}
          >
            {isOut ? 'Completed' : 'Pending'}
          </Tag>
        );
      },
    },
    {
      title: 'BOX TYPE',
      key: 'box_type',
      width: 80,
      render: (record) => (
        <Text style={{ fontSize: '12px' }}>{record.box_type || '-'}</Text>
      ),
    },
    
    {
      title: 'GROSS WEIGHT',
      key: 'weight',
      width: 100,
      render: (record) => {
        const grossWeight = getGrossWeight(record);
        
        return (
          <div style={{ lineHeight: 1.4 }}>
            <Text strong style={{ fontSize: '12px' }}>{grossWeight.toFixed(2)} kg</Text>
          </div>
        );
      },
    },
    {
      title: 'FINAL WEIGHT',
      key: 'final_weight',
      width: 80,
      render: (record) => (
        <Text style={{ fontSize: '12px' }}>
          {record.final_package_weight ? `${parseFloat(record.final_package_weight).toFixed(2)} kg` : '-'}
        </Text>
      ),
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 72,
      fixed: 'right',
      render: (record) => {
        const status = record.stock_issues?.length > 0
          ? { text: 'Stock Issue', className: 'orders-action-status--stock' }
          : null;

        return (
          <div className="orders-action-cell">
            <Space size={0} className="orders-action-buttons">
              <Tooltip title="View Order">
                <Button
                  type="text"
                  icon={<EyeOutlined style={{ fontSize: '15px' }} />}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOrder(record);
                    setOrderDetailsVisible(true);
                  }}
                />
              </Tooltip>
              <Tooltip title="Edit Order">
                <Button
                  type="text"
                  aria-label="Edit Order"
                  icon={<EditOutlined style={{ fontSize: '14px' }} />}
                  size="small"
                  onClick={(e) => { e.stopPropagation(); handleEditOrder(record); }}
                />
              </Tooltip>
            </Space>
            {status && <span className={`orders-action-status ${status.className}`}>{status.text}</span>}
          </div>
        );
      },
    },
  ];

  const orderColumns = [
    {
      title: '#',
      key: 'sequence',
      width: 48,
      render: (_, __, index) => <Text type="secondary">{index + 1}</Text>
    },
    {
      title: 'ORDER ID',
      key: 'order_id',
      width: 140,
      fixed: 'left',
      render: (record) => (
        <Space size={6}>
          <Text strong>{record.orderID || 'N/A'}</Text>
          <Tooltip title="Copy Order ID">
            <CopyOutlined className="orders-copy-icon" onClick={(e) => { e.stopPropagation(); handleCopy(record.orderID); }} />
          </Tooltip>
        </Space>
      )
    },
    {
      title: 'DATE & TIME',
      key: 'date_time',
      width: 132,
      render: (record) => <Text type="secondary">{record.order_date ? moment(record.order_date).format('DD MMM YYYY, hh:mm A') : 'N/A'}</Text>
    },
    {
      title: 'CUSTOMER',
      key: 'customer',
      width: 160,
      render: (record) => (
        <div className="orders-table-customer">
          <div className="orders-customer-identity">
            <Avatar className="orders-customer-avatar" size={26}>
              {getCustomerInitial(record.customer?.name)}
            </Avatar>
            <Text strong ellipsis={{ tooltip: record.customer?.name || 'N/A' }}>
              {record.customer?.name || 'N/A'}
            </Text>
          </div>
          <Text type="secondary">{record.customer?.mobile || 'N/A'}</Text>
        </div>
      )
    },
    {
      title: 'CUSTOMER TAGS',
      key: 'customer_tags',
      width: 150,
      render: (record) => {
        const customerTags = getCustomerTags(record);

        return customerTags.length ? (
          <div className="orders-customer-tags">
            {customerTags.slice(0, 3).map(tagName => (
              <Tag key={tagName} color={getTagColor(tagName)}>{tagName}</Tag>
            ))}
            {customerTags.length > 3 && <Tag>+{customerTags.length - 3}</Tag>}
          </div>
        ) : (
          <Text type="secondary" className="orders-customer-tags-empty">No tags</Text>
        );
      }
    },
    {
      title: 'COURIER',
      key: 'courier',
      width: 140,
      render: (record) => {
        const courierMeta = getCourierMeta(record.shipments?.[0]?.courier_name);

        return (
          <Space size={8}>
            {courierMeta.image ? (
              <img
                src={courierMeta.image}
                alt={courierMeta.label}
                style={{
                  width: 28,
                  height: 20,
                  objectFit: 'contain',
                  display: 'block',
                  borderRadius: 4,
                  background: '#fff'
                }}
              />
            ) : (
              <TruckOutlined className="orders-courier-icon" />
            )}
            <Text>{courierMeta.label}</Text>
          </Space>
        );
      }
    },
    {
      title: 'AWB',
      key: 'awb',
      width: 170,
      render: (record) => {
        const awb = record.shipments?.[0]?.awb_code;
        if (editingCell?.id === record.orderID && editingCell?.field === 'awb_code') {
          return <Input size="small" autoFocus value={inlineValue} onChange={e => setInlineValue(e.target.value)} onPressEnter={saveInlineEdit} onBlur={saveInlineEdit} onClick={e => e.stopPropagation()} />;
        }
        return (
          <Space size={5}>
            <Text className="orders-awb-value">{awb || 'N/A'}</Text>
            {awb && <Tooltip title="Copy AWB"><CopyOutlined className="orders-copy-icon" onClick={(e) => { e.stopPropagation(); handleCopy(awb); }} /></Tooltip>}
            <Tooltip title="Edit AWB"><EditOutlined className="orders-inline-icon" onClick={(e) => { e.stopPropagation(); startInlineEdit(record, 'awb_code', awb); }} /></Tooltip>
          </Space>
        );
      }
    },
    {
      title: 'PICKUP STATUS',
      key: 'pickup',
      width: 132,
      render: (record) => <Tag color={getPickupStatus(record) === 'Pickup Scheduled' ? 'processing' : 'default'}>{getPickupStatus(record)}</Tag>
    },
    {
      title: (
        <span className="orders-sortable-column-title">
          <span>PACKING STATUS</span>
          <Tooltip title={sortConfig.field === 'packed_status' && sortConfig.order === 'ascend' ? 'Sort descending' : 'Sort ascending'}>
            <Button
              type="text"
              size="small"
              className="orders-column-sort-button"
              aria-label="Sort packing status"
              icon={<SortAscendingOutlined className={sortConfig.field === 'packed_status' ? 'orders-sort-active' : ''} />}
              onClick={(event) => { event.stopPropagation(); handleSort('packed_status'); }}
            />
          </Tooltip>
        </span>
      ),
      key: 'packing',
      width: 140,
      render: (record) => getPackingStatus(record.packed_status)
    },
    {
      title: 'PACKED BY',
      key: 'packed_by',
      width: 145,
      render: (record) => record.packed_person_name ? (
        <div className="orders-packed-by">
          <Text strong>{record.packed_person_name}</Text>
          <Text type="secondary">{record.packed_date ? moment(record.packed_date).format('DD MMM, hh:mm A') : 'N/A'}</Text>
        </div>
      ) : <Text type="secondary">N/A</Text>
    },
    {
      title: 'WAREHOUSE',
      key: 'warehouse',
      width: 112,
      render: (record) => {
        const warehouseStatus = record.warehouse || record.warehouse_name || (record.warehouse_out === 'Yes' || record.warehouse_out_date ? 'Completed' : 'Pending');
        return <Tag color={warehouseStatus === 'Pending' ? 'default' : 'success'}>{warehouseStatus}</Tag>;
      }
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 110,
      fixed: 'right',
      render: (record) => {
        const isSyncEligible = !['Completed', 'Cancelled'].includes(record.packed_status);

        const actionContent = (
          <Space size={2} className="orders-action-buttons">
            <Tooltip title="View Order">
              <Button type="text" size="small" aria-label="View Order" icon={<EyeOutlined />} onClick={(e) => { e.stopPropagation(); setSelectedOrder(record); setOrderDetailsVisible(true); }} />
            </Tooltip>
            <Tooltip title="Edit Order">
              <Button type="text" size="small" aria-label="Edit Order" icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); handleEditOrder(record); }} />
            </Tooltip>
            {isSyncEligible && (
              <Tooltip title="Re-sync order from Odoo">
                <Button
                  type="text"
                  size="small"
                  aria-label="Re-sync Order"
                  icon={<SyncOutlined />}
                  loading={false}
                  onClick={async (e) => {
                    e.stopPropagation();
                    try {
                      await syncSingleOrder(record);
                      if (onRefresh) onRefresh();
                    } catch (error) {
                      message.error(error.message || 'Order sync failed');
                    }
                  }}
                />
              </Tooltip>
            )}
          </Space>
        );

        let statusLabel = null;
        if (record.stock_issues?.length > 0) {
          statusLabel = <span className="orders-action-status orders-action-status--stock">Stock Issue</span>;
        } else if (record.packed_status === 'Hold') {
          statusLabel = <span className="orders-action-status orders-action-status--hold">On Hold</span>;
        } else if (record.packed_status === 'Cancelled') {
          statusLabel = <span className="orders-action-status orders-action-status--cancelled">Cancelled</span>;
        }

        if (!statusLabel) {
          return actionContent;
        }

        return (
          <div className="orders-action-cell">
            {actionContent}
            {statusLabel}
          </div>
        );
      }
    }
  ];

  const columns = orderColumns.filter(col => visibleColumns.includes(col.key));

  const expandedRowRender = (record) => {
    // Timeline Logic
    let currentStep = 0;
    const shipmentStatus = record.shipments?.[0]?.status?.toUpperCase();
    
    if (record.packed_status === 'Completed') {
      currentStep = 1;
    }
    
    const shippedStatuses = ['IN TRANSIT', 'OUT FOR DELIVERY', 'PICKED UP', 'OUT FOR PICKUP', 'DELIVERED', 'SHIPPED'];
    if (record.warehouse_out === 'Yes' || shippedStatuses.includes(shipmentStatus)) {
      currentStep = 2;
    }
    
    if (shipmentStatus === 'DELIVERED') {
      currentStep = 3;
    }

   
    return (
      <div style={{ padding: '20px', background: darkTheme ? '#1f1f1f' : '#fafafa', borderRadius: '8px' }}>        
        {/* Timeline Visualization */}
        <div style={{ marginBottom: 24, padding: '0 20px' }}>
          <Steps
            current={currentStep}
            size="small"
            items={[
              { title: 'Placed', description: moment(record.order_date).format('DD MMM') },
              { title: 'Packed', description: record.packed_date ? moment(record.packed_date).format('DD MMM') : '' },
              { title: 'Shipped', description: record.warehouse_out_date ? moment(record.warehouse_out_date).format('DD MMM') : '' },
              { title: 'Delivered' },
            ]}
          />
        </div>

        {/* Additional Details (Non-repeated info) */}
        <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
          {record.box_type && (
            <Col span={6}>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>Box Type</Text>
              <Tag color="geekblue" style={{ fontSize: '13px', padding: '4px 10px' }}>{record.box_type}</Tag>
            </Col>
          )}
          {record.packed_person_name && (
             <Col span={6}>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>Packed By</Text>
              <div style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: 6, fontWeight: 500 }}>
                📅 {record.packed_date ? moment(record.packed_date).format('DD MMM, hh:mm A') : 'N/A'}
              </div>
              <div style={{ fontWeight: 600, fontSize: '13px' }}>
                <UserOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                {record.packed_person_name}
              </div>
            </Col>
          )}
          {(record.warehouse_out_date || record.warehouse_out_by) && (
             <Col span={6}>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>Warehouse Out</Text>
              <div style={{ fontWeight: 600, fontSize: '13px' }}>
                <ClockCircleOutlined style={{ marginRight: 6, color: '#52c41a' }} />
                {record.warehouse_out_date ? moment(record.warehouse_out_date).format('DD MMM, hh:mm A') : 'Date not available'}
              </div>
              <div style={{ fontSize: '12px', color: '#595959', marginTop: 6 }}>
                <UserOutlined style={{ marginRight: 6, color: '#1890ff' }} />
                <span style={{ marginRight: 4 }}>Sent by:</span>
                {record.warehouse_out_by || 'N/A'}
              </div>
            </Col>
          )}
          {record.final_package_weight && (
             <Col span={6}>
              <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>Final Weight</Text>
              <div style={{ fontWeight: 600, fontSize: '13px' }}>
                <BoxPlotOutlined style={{ marginRight: 6, color: '#faad14' }} />
                {parseFloat(record.final_package_weight).toFixed(2)} kg
              </div>
            </Col>
          )}
          <Col span={6}>
            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>Gross Weight</Text>
            <div style={{ fontWeight: 600, fontSize: '13px' }}>
              <BoxPlotOutlined style={{ marginRight: 6, color: '#52c41a' }} />
              {getGrossWeight(record).toFixed(2)} kg
            </div>
          </Col>
        </Row>

        {record.hold_reason && (
          <div style={{ 
            marginBottom: 24, 
            padding: '16px', 
            borderRadius: '8px', 
            background: darkTheme ? 'rgba(255, 169, 64, 0.1)' : '#fffbe6', 
            border: `1px solid ${darkTheme ? '#d48806' : '#ffe58f'}`,
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start'
          }}>
            <WarningOutlined style={{ color: '#faad14', fontSize: '20px', marginTop: '2px' }} />
            <div>
              <Text strong style={{ color: '#d48806', display: 'block', marginBottom: '4px', fontSize: '12px', letterSpacing: '0.5px' }}>ORDER ON HOLD</Text>
              <Text style={{ color: darkTheme ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.65)' }}>{record.hold_reason}</Text>
            </div>
          </div>
        )}

        {record.stock_issues && record.stock_issues.length > 0 && (
          <div style={{ 
            marginBottom: 24, 
            padding: '16px', 
            borderRadius: '8px', 
            background: darkTheme ? 'rgba(255, 77, 79, 0.1)' : '#fff1f0', 
            border: `1px solid ${darkTheme ? '#a8071a' : '#ffa39e'}`,
            display: 'flex',
            gap: '12px',
            alignItems: 'flex-start'
          }}>
            <InboxOutlined style={{ color: '#ff4d4f', fontSize: '20px', marginTop: '2px' }} />
            <div>
              <Text strong style={{ color: '#cf1322', display: 'block', marginBottom: '8px', fontSize: '12px', letterSpacing: '0.5px' }}>STOCK ISSUES DETECTED</Text>
              <Space wrap size={[4, 8]}>
                {record.stock_issues.map((item, i) => (
                  <Tag key={i} color="error" style={{ borderRadius: '4px', fontWeight: 500 }}>{item}</Tag>
                ))}
              </Space>
            </div>
          </div>
        )}

        {/* Products List - Redesigned */}
        <div style={{ 
          background: darkTheme ? '#141414' : '#fff', 
          borderRadius: '8px', 
          border: `1px solid ${darkTheme ? '#303030' : '#f0f0f0'}`, 
          overflow: 'hidden' 
        }}>
          <div style={{ 
            padding: '12px 20px', 
            borderBottom: `1px solid ${darkTheme ? '#303030' : '#f0f0f0'}`, 
            display: 'flex', 
            justifyContent: 'space-between', 
            background: darkTheme ? '#1f1f1f' : '#fafafa' 
          }}>
            <Text strong>Products ({record.products?.length || 0})</Text>
            <Text type="secondary">Total Weight: {getOrderTotalWeight(record).toFixed(2)} kg</Text>
          </div>
          
          {record.products?.map((product, idx) => (
            <div key={idx} style={{ 
              padding: '16px 20px', 
              borderBottom: idx === record.products.length - 1 ? 'none' : `1px solid ${darkTheme ? '#303030' : '#f0f0f0'}`, 
              display: 'flex', 
              alignItems: 'center', 
              gap: 16 
            }}>
              {/* Image */}
              <Avatar 
                src={product.imageUrl} 
                shape="square" 
                size={56} 
                style={{ border: `1px solid ${darkTheme ? '#434343' : '#f0f0f0'}`, background: darkTheme ? '#262626' : '#f5f5f5', flexShrink: 0 }}
                icon={<ShoppingOutlined style={{ color: '#bfbfbf' }} />}
              />
              
              {/* Details */}
              <div style={{ flexGrow: 1, minWidth: 0 }}>
                <Text strong style={{ fontSize: '15px', display: 'block', marginBottom: 8, color: darkTheme ? '#fff' : '#262626' }} ellipsis={{ tooltip: product.name }}>
                  {product.name}
                </Text>
                <Space wrap size={[8, 4]}>
                  {product.sku && <Tag icon={<BarcodeOutlined />} color="blue" style={{ borderRadius: '4px' }}>{`SKU: ${product.sku}`}</Tag>}
                  {product.productCategory && <Tag icon={<AppstoreOutlined />} color="purple" style={{ borderRadius: '4px' }}>{product.productCategory}</Tag>}
                  {product.updated_id && <Tag icon={<BarcodeOutlined />} color="cyan" style={{ borderRadius: '4px' }}>{`Barcode: ${product.updated_id}`}</Tag>}
                </Space>
              </div>

              {/* Qty */}
              <div style={{ flexShrink: 0, textAlign: 'right', minWidth: '80px' }}>
                <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginBottom: 4 }}>QUANTITY</Text>
                <Text strong style={{ fontSize: '18px', color: darkTheme ? '#1890ff' : '#096dd9' }}>{product.quantity || 1}</Text>
              </div>
            </div>
          ))}
        </div>       
      </div>
    );
  };

  const StatsCard = ({ title, value, icon, color, suffix, prefix, supportingText, onClick, className, style }) => (
    <div 
      onClick={onClick}
      className={`orders-kpi-card ${className || ''}`}
      style={{ 
        cursor: onClick ? 'pointer' : 'default',
        ...style
      }}
    >
      <div className="orders-kpi-card-header">
        <div className="orders-kpi-icon" style={{ color, background: `${color}14` }}>
          {React.cloneElement(icon, { style: { color, fontSize: '15px' } })}
        </div>
        <Text className="orders-kpi-label">{title}</Text>
      </div>
      <div className="orders-kpi-bottom">
        {prefix && <Text className="orders-kpi-prefix">{prefix}</Text>}
        <Text className="orders-kpi-value" style={{ color }}>{value}</Text>
        {suffix && <Text className="orders-kpi-suffix">{suffix}</Text>}
        <span className="orders-kpi-separator" aria-hidden="true">•</span>
        <Text className="orders-kpi-supporting">{supportingText}</Text>
      </div>
    </div>
  );

  // ── Past Order Search ──────────────────────────────────────────
  const searchPastOrder = async () => {
    if (!pastOrderIdSearch.trim()) {
      message.warning("Enter an Order ID to search past orders.");
      return;
    }
    setPastOrderLoading(true);
    setPastOrderResult(null);
    try {
      const response = await axios.post(`${config.apiBaseUrl}/orders/past-orders`, {
        orderID: pastOrderIdSearch.trim(),
      });
      const order = response.data?.order;
      if (order) {
        setPastOrderResult(order);
        message.success(`Past order ${order.orderID} loaded successfully.`);
      } else {
        message.warning("Past order not found.");
      }
    } catch (err) {
      message.error(err.response?.data?.message || "Failed to fetch past order.");
    } finally {
      setPastOrderLoading(false);
    }
  };

  const clearAllFilters = () => {
    setSearchText('');
    setPastOrderIdSearch('');
    setPastOrderResult(null);
    setDateRange(null);
    setPackedDateRange(null);
    setSelectedCouriers([]);
    setSelectedProducts([]);
    setPackingStatusFilter([]);
    setShipmentStatusFilter([]);
    setAwbStatusFilter(null);
    setHoldReasonFilter("");
    setStockIssuesFilter([]);
    setSortConfig({ field: 'order_date', order: 'descend' });
  };

  const handleStatClick = (type) => {
    clearAllFilters();
    switch (type) {
      case 'completed':
        setPackingStatusFilter(['Completed']);
        break;
      case 'pending':
        setDateRange([moment().startOf('day').toDate(), moment().endOf('day').toDate()]);
        setPackingStatusFilter(['Not Completed']);
        break;
      case 'hold':
        setPackingStatusFilter(['Hold']);
        break;
      case 'cancelled':
        setPackingStatusFilter(['Cancelled']);
        break;
      case 'today':
        setDateRange([moment().startOf('day').toDate(), moment().endOf('day').toDate()]);
        break;
      case 'missingAwbToday':
        setDateRange([moment().startOf('day').toDate(), moment().endOf('day').toDate()]);
        setAwbStatusFilter('Without AWB');
        break;
      case 'overallNotCompleted':
        setPackingStatusFilter(['Not Completed', 'Pending']);
        break;
      default:
        break;
    }
  };

  // Extract unique values for filters
  // For couriers: only show carriers that exist in date-filtered orders
  const dateFilteredOrders = useMemo(() => {
    if (!dateRange || !dateRange[0]) return localOrders;
    
    const startDate = moment(dateRange[0]).startOf("day");
    const endDate = dateRange[1] ? moment(dateRange[1]).endOf("day") : moment(dateRange[0]).endOf("day");
    
    return localOrders.filter(order => {
      if (!order.createdAt) return false;
      const packingDate = moment(order.createdAt);
      return packingDate.isBetween(startDate, endDate, null, "[]");
    });
  }, [localOrders, dateRange]);

  const uniqueCouriers = useMemo(() => Array.from(new Set(
    dateFilteredOrders.map(o => o.shipments?.[0]?.courier_name).filter(Boolean)
  )).sort(), [dateFilteredOrders]);

  const uniqueProducts = useMemo(() => Array.from(
    new Map(
      localOrders.flatMap(o => o.products || []).filter(p => p && p.name).map(p => [p.name, p])
    ).values()
  ).sort((a, b) => a.name.localeCompare(b.name)), [localOrders]);

  const uniquePackingStatus = useMemo(() => Array.from(new Set(
    localOrders.map(o => o.packed_status).filter(status => !isEmptyPackingStatus(status))
  )).sort().concat(
    [PACKING_STATUS_NOT_AVAILABLE]
  ), [localOrders]);

  const getPackingStatusFilterLabel = (status) =>
    status === PACKING_STATUS_NOT_AVAILABLE ? 'N/A' : status;

  const uniqueStockIssues = useMemo(() => Array.from(new Set(
    localOrders.flatMap(o => o.stock_issues || []).filter(Boolean)
  )).sort(), [localOrders]);

  const uniqueShipmentStatus = useMemo(() => Array.from(new Set(
    localOrders.map(o => o.shipments?.[0]?.status).filter(Boolean)
  )).sort(), [localOrders]);

  // Presentation-only helper. It counts how many of the EXISTING filters are
  // currently active so the toolbar badge can show it. It only reads the
  // existing filter state and never changes how any filter is applied.
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (dateRange && dateRange[0]) count += 1;
    if (packedDateRange && packedDateRange[0]) count += 1;
    if (awbStatusFilter) count += 1;
    if (packingStatusFilter.length) count += packingStatusFilter.length;
    if (shipmentStatusFilter.length) count += shipmentStatusFilter.length;
    if (selectedCouriers.length) count += selectedCouriers.length;
    if (selectedProducts.length) count += selectedProducts.length;
    if (stockIssuesFilter.length) count += stockIssuesFilter.length;
    if (holdReasonFilter.trim()) count += 1;
    return count;
  }, [
    dateRange,
    packedDateRange,
    awbStatusFilter,
    packingStatusFilter,
    shipmentStatusFilter,
    selectedCouriers,
    selectedProducts,
    stockIssuesFilter,
    holdReasonFilter,
  ]);

  // Removed saved views preset functionality to avoid date type issues

  // Manifest PDF (print) for selected date range, grouped by courier
  const handlePrintManifest = () => {
    const manifestOrders = filteredOrders; // assume filters already applied to dateRange
    const groups = manifestOrders.reduce((acc, o) => {
      const courier = o.shipments?.[0]?.courier_name || 'Unknown Courier';
      (acc[courier] = acc[courier] || []).push(o);
      return acc;
    }, {});

    const win = window.open('', '_blank');
    if (!win) return;
    const dateLabel = dateRange && dateRange[0] && dateRange[1]
      ? `${moment(dateRange[0]).format('DD MMM YYYY')} - ${moment(dateRange[1]).format('DD MMM YYYY')}`
      : `${moment().format('DD MMM YYYY')}`;

    const styles = `
      <style>
        body { font-family: Arial, sans-serif; color: #000; }
        .header { display:flex; justify-content: space-between; align-items:center; margin-bottom: 12px; }
        .title { font-size: 18px; font-weight: 600; }
        .date { font-size: 12px; color: #555; }
        .group { margin-bottom: 24px; }
        .group h3 { margin: 8px 0; font-size: 14px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; font-size: 12px; }
        th { background: #f0f0f0; text-align: left; }
        tfoot td { font-weight: 600; }
        .sign { margin-top: 16px; display:flex; justify-content: space-between; }
        .sign div { width: 45%; border-top: 1px solid #000; padding-top: 6px; font-size: 12px; }
        @media print { .no-print { display: none; } }
      </style>
    `;

    let html = `
      <!doctype html>
      <html>
      <head>
        <meta charset="utf-8" />
        <title>Manifest</title>
        ${styles}
      </head>
      <body>
        <div class="header">
          <div class="title">Shipping Manifest</div>
          <div class="date">Date: ${dateLabel}</div>
        </div>
    `;

    Object.entries(groups).forEach(([courier, list]) => {
      html += `
        <div class="group">
          <h3>${courier} — Count: ${list.length}</h3>
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Order ID</th>
                <th>AWB</th>
                <th>Box Type</th>
                <th>Weight (kg)</th>
              </tr>
            </thead>
            <tbody>
      `;
      list.forEach((o, idx) => {
        const awb = o.shipments?.[0]?.awb_code || '';
        const box = o.box_type || 'N/A';
        const weight = (getOrderTotalWeight(o) || 0).toFixed(2);
        html += `
          <tr>
            <td>${idx + 1}</td>
            <td>${o.orderID || ''}</td>
            <td>${awb}</td>
            <td>${box}</td>
            <td>${weight}</td>
          </tr>
        `;
      });
      const totalWeight = list.reduce((sum, o) => sum + (getOrderTotalWeight(o) || 0), 0).toFixed(2);
      html += `
            </tbody>
            <tfoot>
              <tr>
                <td colspan="4">Total</td>
                <td>${totalWeight}</td>
              </tr>
            </tfoot>
          </table>
          <div class="sign">
            <div>Warehouse Signature</div>
            <div>Courier Signature</div>
          </div>
        </div>
      `;
    });

    html += `
        <button class="no-print" onclick="window.print()">Print / Save as PDF</button>
      </body>
      </html>
    `;

    win.document.open();
    win.document.write(html);
    win.document.close();
  };

  // Popover Content
  const columnsContent = (
    <div style={{ width: 280 }}>
      <Checkbox.Group 
        style={{ display: 'flex', flexDirection: 'column', gap: 12 }}
        value={visibleColumns}
        onChange={setVisibleColumns}
      >
        <div>
          <Checkbox value="sequence">#</Checkbox>
        </div>
        <div>
          <Checkbox value="order_id">Order ID</Checkbox>
        </div>
        <div>
          <Checkbox value="date_time">Date & Time</Checkbox>
        </div>
        <div>
          <Checkbox value="customer">Customer</Checkbox>
        </div>
        <div>
          <Checkbox value="customer_tags">Customer Tags</Checkbox>
        </div>
        <div>
          <Checkbox value="courier">Courier</Checkbox>
        </div>
        <div>
          <Checkbox value="awb">AWB</Checkbox>
        </div>
        <div>
          <Checkbox value="pickup">Pickup Status</Checkbox>
        </div>
        <div>
          <Checkbox value="packing">Packing Status</Checkbox>
        </div>
        <div>
          <Checkbox value="packed_by">Packed By</Checkbox>
        </div>
        <div>
          <Checkbox value="warehouse">Warehouse</Checkbox>
        </div>
        <div>
          <Checkbox value="box_type">Box Type</Checkbox>
        </div>
        <div>
          <Checkbox value="actions">Actions</Checkbox>
          <Text type="secondary" style={{ fontSize: '11px', display: 'block', marginLeft: 24, marginTop: 2 }}>View • More actions</Text>
        </div>
      </Checkbox.Group>
    </div>
  );

  const viewsContent = (
    <div style={{ width: 300 }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Input 
          placeholder="View Name" 
          value={viewName} 
          onChange={e => setViewName(e.target.value)}
        />
        <Button type="primary" icon={<SaveOutlined />} onClick={handleSaveView} />
      </div>
      <List
        size="small"
        dataSource={savedViews}
        renderItem={item => (
          <List.Item
            actions={[
              <Button type="text" size="small" icon={<DeleteOutlined />} danger onClick={() => handleDeleteView(item.id)} />,
              <Button type="link" size="small" onClick={() => handleApplyView(item)}>Apply</Button>
            ]}
          >
            {item.name}
          </List.Item>
        )}
        locale={{ emptyText: "No saved views" }}
      />
    </div>
  );

  return (
    <div className="orders-page" style={{ minHeight: '100vh'}}>
      <style>{`
        .delayed-row td {
          background-color: #fff1f0 !important;
        }
        .dark-theme .delayed-row td {
          background-color: #4a1212 !important;
        }
        @keyframes border-rotate {
          0% { background-position: 0% 50%; }
          100% { background-position: 100% 50%; }
        }
        .danger-rotating-border {
          border: 3px solid transparent !important;
          background: linear-gradient(var(--bg-primary, #fff), var(--bg-primary, #fff)) padding-box,
                      linear-gradient(90deg, #ff4d4f, #ff7875, #ff4d4f) border-box !important;
          background-size: 200% 100% !important;
          animation: border-rotate 1.5s linear infinite;
          box-shadow: 0 0 15px rgba(255, 77, 79, 0.6);
        }
      `}</style>
      {/* Header with Stats */}
      <Card
        className="orders-summary-card"
        style={{ 
          marginBottom: 16,
          border: 'none',
          boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',         
          borderRadius: '12px'
        }}
        bodyStyle={{ padding: '20px 24px' }}
      >
        <Row justify="space-between" align="middle" gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} md={8}>
            <Space direction="vertical" size={0}>
              <Title level={4} style={{ margin: 0, fontWeight: 600 }}>Orders Dashboard</Title>
              <Text type="secondary" style={{ fontSize: '13px' }}>
                {moment().format('MMMM DD, YYYY')} • {ordersWithPastOrder.length} orders found
              </Text>
            </Space>
          </Col>
          <Col xs={24} md={8} className="orders-heading-search-col">
            <Search
              ref={ordersSearchRef}
              className="orders-toolbar-search"
              style={{ flex: 1, minWidth: 0 }}
              placeholder="Search orders by Order ID, Customer, AWB, Phone..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              onSearch={val => setSearchText(val)}
              allowClear
            />
            <Search
              className="orders-toolbar-search"
              style={{ flex: 1, minWidth: 0 }}
              placeholder="Search past order..."
              value={pastOrderIdSearch}
              onChange={e => setPastOrderIdSearch(e.target.value)}
              onSearch={() => searchPastOrder()}
              loading={pastOrderLoading}
              enterButton="Search Past"
              allowClear
            />
          </Col>
          <Col xs={24} md={8} className="orders-heading-actions-col">
            <Space className="orders-toolbar-actions" wrap size={[8, 8]}>
              <Popover
                placement="bottomRight"
                trigger="click"
                content={(
                  <div className="orders-shortcuts-popover">
                    <Text strong>Keyboard shortcuts</Text>
                    <div><kbd>/</kbd><span>Focus order search</span></div>
                    <div><kbd>V</kbd><span>View selected order</span></div>
                    <div><kbd>E</kbd><span>Edit selected order</span></div>
                    <div><kbd>R</kbd><span>Refresh orders</span></div>
                    <div><kbd>Esc</kbd><span>Close active panel</span></div>
                    <Text type="secondary">V and E require exactly one selected order.</Text>
                  </div>
                )}
              >
                <Button
                  className="orders-toolbar-button orders-shortcuts-button"
                  icon={<QuestionCircleOutlined />}
                  aria-label="Keyboard shortcuts"
                >
                  {!screens.xs && 'Shortcuts'}
                </Button>
              </Popover>
              <Popover content={columnsContent} trigger="click" placement="bottomRight" title="Show Columns"><Button className="orders-toolbar-button" icon={<TableOutlined />}>{!screens.xs && 'Columns'}</Button></Popover>
              <Popover content={viewsContent} trigger="click" placement="bottomRight" title="Saved Views" open={saveViewOpen} onOpenChange={setSaveViewOpen}><Button className="orders-toolbar-button" icon={<SaveOutlined />}>{!screens.xs && 'Views'}</Button></Popover>
              <Button className="orders-toolbar-button" icon={<ReloadOutlined spin={isRefreshing} />} onClick={onRefresh} loading={isRefreshing}>{!screens.xs && 'Reload'}</Button>
              <Dropdown overlay={<Menu><Menu.Item key="manifest" onClick={handlePrintManifest}>Manifest PDF</Menu.Item><Menu.Item key="export" icon={<DownloadOutlined />} onClick={() => handleExportExcel(filteredOrders)}>Export All</Menu.Item><Menu.Item key="bulkAwb" icon={<UploadOutlined />} onClick={() => setBulkAwbModalVisible(true)}>Bulk AWB Update</Menu.Item></Menu>}><Button className="orders-toolbar-button">{screens.xs ? <DownOutlined /> : <>Actions <DownOutlined /></>}</Button></Dropdown>
            </Space>
          </Col>
        </Row>

        {activeFilterCount > 0 && (
          <div className="orders-active-chips">
            <Space size={[8, 8]} wrap align="center">
              <Text type="secondary" className="orders-active-chips-label">
                <FilterOutlined /> Active
              </Text>
              {dateRange && dateRange[0] && (
                <Tag className="orders-chip" closable onClose={() => setDateRange(null)}>
                  <CalendarOutlined /> {formatFilterDate(dateRange[0])}
                  {dateRange[1] ? ` - ${formatFilterDate(dateRange[1])}` : ''}
                </Tag>
              )}
              {packedDateRange && packedDateRange[0] && (
                <Tag className="orders-chip" closable onClose={() => setPackedDateRange(null)}>
                  <BoxPlotOutlined /> Packed {formatFilterDate(packedDateRange[0])}
                  {packedDateRange[1] ? ` - ${formatFilterDate(packedDateRange[1])}` : ''}
                </Tag>
              )}
              {awbStatusFilter && (
                <Tag className="orders-chip" closable onClose={() => setAwbStatusFilter(null)}>
                  <BarcodeOutlined /> {awbStatusFilter}
                </Tag>
              )}
              {packingStatusFilter.map(status => (
                <Tag
                  key={`packing-${status}`}
                  className="orders-chip"
                  closable
                  onClose={() => setPackingStatusFilter(prev => prev.filter(s => s !== status))}
                >
                  <CheckCircleOutlined /> {getPackingStatusFilterLabel(status)}
                </Tag>
              ))}
              {shipmentStatusFilter.map(status => (
                <Tag
                  key={`shipment-${status}`}
                  className="orders-chip"
                  closable
                  onClose={() => setShipmentStatusFilter(prev => prev.filter(s => s !== status))}
                >
                  <GlobalOutlined /> {status}
                </Tag>
              ))}
              {selectedCouriers.map(courier => (
                <Tag
                  key={`courier-${courier}`}
                  className="orders-chip"
                  closable
                  onClose={() => setSelectedCouriers(prev => prev.filter(c => c !== courier))}
                >
                  <TruckOutlined /> {courier}
                </Tag>
              ))}
              {selectedProducts.map(product => (
                <Tag
                  key={`product-${product}`}
                  className="orders-chip"
                  closable
                  onClose={() => setSelectedProducts(prev => prev.filter(p => p !== product))}
                >
                  <ShoppingOutlined /> {product}
                </Tag>
              ))}
              {holdReasonFilter.trim() && (
                <Tag className="orders-chip" closable onClose={() => setHoldReasonFilter('')}>
                  <WarningOutlined /> {holdReasonFilter}
                </Tag>
              )}
              {stockIssuesFilter.map(issue => (
                <Tag
                  key={`issue-${issue}`}
                  className="orders-chip"
                  closable
                  onClose={() => setStockIssuesFilter(prev => prev.filter(i => i !== issue))}
                >
                  <InboxOutlined /> {issue}
                </Tag>
              ))}
              <Button type="link" size="small" className="orders-chip-clear" onClick={clearAllFilters}>
                Clear all
              </Button>
            </Space>
          </div>
        )}

        <div className="orders-kpi-grid">
            <StatsCard
              title="Today AWB's Missing  "
              value={dashboardStats.missingAwbToday}
              color="#f5222d"
              icon={<FileTextOutlined />}
              supportingText="Orders requiring action"
              onClick={() => handleStatClick('missingAwbToday')}
            />
            <StatsCard
              title="Today Packing Secheduled"
              value={dashboardStats.today}
              color="#722ed1"
              icon={<CalendarOutlined />}
              supportingText="Today's schedule"
              onClick={() => handleStatClick('today')}
            />
            <StatsCard
              title="Today Pending Orders"
              value={dashboardStats.pending}
              color="#faad14"
              icon={<ClockCircleOutlined />}
              supportingText="Needs action"
              onClick={() => handleStatClick('pending')}
            />
            
            <StatsCard
              title="Total On Hold"
              value={dashboardStats.hold}
              color="#fa541c"
              icon={<WarningOutlined />}
              supportingText="Orders on hold"
              onClick={() => handleStatClick('hold')}
            />
            <StatsCard
              title="Total Cancelled"
              value={dashboardStats.cancelled}
              color="#d9534f"
              icon={<CloseOutlined />}
              supportingText="Cancelled orders"
              onClick={() => handleStatClick('cancelled')}
            />
            <StatsCard
              title="Total Orders"
              value={dashboardStats.total}
              color="#1890ff"
              icon={<AppstoreOutlined />}
              supportingText="All orders"
              onClick={() => handleStatClick('total')}
            />
            <StatsCard
              title="Completed"
              value={dashboardStats.completed}
              color="#52c41a"
              icon={<CheckCircleOutlined />}
              supportingText="Completed orders"
              onClick={() => handleStatClick('completed')}
            />
            <StatsCard
              title=" Last 30 days Overall Pending"
              value={dashboardStats.overallNotCompleted}
              color="#eb2f96"
              icon={<ClockCircleOutlined />}
              supportingText="Overall pending"
              onClick={() => handleStatClick('overallNotCompleted')}
            />
        </div>
      </Card>

      <section className="orders-primary-filter-bar" aria-label="Order filters">
        <div className="orders-primary-filter-row">
          <div className="orders-primary-filter-field orders-primary-date-field">
            <Text className="orders-primary-filter-label">Start Date / End Date</Text>
            <Calendar
              value={dateRange}
              onChange={handleDateChange}
              selectionMode="range"
              readOnlyInput
              placeholder="Start Date  -  End Date"
              className="orders-primary-calendar"
              showIcon
              dateFormat="dd/mm/yy"
            />
          </div>
          <div className="orders-primary-filter-field orders-primary-date-field">
            <Text className="orders-primary-filter-label">Packed Start / Packed End</Text>
            <Calendar
              value={packedDateRange}
              onChange={(e) => setPackedDateRange(e.value)}
              selectionMode="range"
              readOnlyInput
              placeholder="Packed Start  -  Packed End"
              className="orders-primary-calendar"
              showIcon
              dateFormat="dd/mm/yy"
            />
          </div>
          <div className="orders-primary-filter-field">
            <Text className="orders-primary-filter-label">AWB Status</Text>
            <Select
              allowClear
              value={awbStatusFilter || undefined}
              onChange={setAwbStatusFilter}
              placeholder="All AWB statuses"
              options={[{ label: 'With AWB', value: 'With AWB' }, { label: 'Without AWB', value: 'Without AWB' }]}
            />
          </div>
          <div className="orders-primary-filter-field orders-primary-filter-field--wide">
            <Text className="orders-primary-filter-label">Packing Status</Text>
            <Select
              mode="multiple"
              allowClear
              maxTagCount={1}
              value={packingStatusFilter}
              onChange={setPackingStatusFilter}
              placeholder="All packing statuses"
              options={uniquePackingStatus.map(status => ({ label: getPackingStatusFilterLabel(status), value: status }))}
            />
          </div>
          <div className="orders-primary-filter-field orders-primary-filter-field--wide">
            <Text className="orders-primary-filter-label">Shipment Status</Text>
            <Select
              mode="multiple"
              allowClear
              maxTagCount={1}
              value={shipmentStatusFilter}
              onChange={setShipmentStatusFilter}
              placeholder="All shipment statuses"
              options={uniqueShipmentStatus.map(status => ({ label: status, value: status }))}
            />
          </div>
          <div className="orders-primary-filter-field">
            <Text className="orders-primary-filter-label">Hold Reason</Text>
            <Select
              allowClear
              showSearch
              value={holdReasonFilter || undefined}
              onChange={setHoldReasonFilter}
              placeholder="All hold reasons"
              options={HOLD_REASONS.map(reason => ({ label: reason, value: reason }))}
            />
          </div>
          <div className="orders-primary-filter-field">
            <Text className="orders-primary-filter-label">Stock Issues</Text>
            <Select
              mode="multiple"
              allowClear
              maxTagCount={1}
              value={stockIssuesFilter}
              onChange={setStockIssuesFilter}
              placeholder="All stock issues"
              options={uniqueStockIssues.map(issue => ({ label: issue, value: issue }))}
            />
          </div>
          <div className="orders-primary-filter-actions">
            <Button type="link" className="orders-clear-link" onClick={clearAllFilters}>Clear All</Button>
            <Button className="orders-toolbar-button" icon={<FilterOutlined />} onClick={() => setFilterDrawerVisible(true)}>More Filters</Button>
          </div>
        </div>
        {activeFilterCount > 0 && (
          <div className="orders-primary-filter-chips">
            <Text type="secondary">Active:</Text>
            {awbStatusFilter && <Tag closable onClose={() => setAwbStatusFilter(null)}>{awbStatusFilter}</Tag>}
            {packingStatusFilter.map(status => <Tag key={`primary-packing-${status}`} closable onClose={() => setPackingStatusFilter(prev => prev.filter(item => item !== status))}>{getPackingStatusFilterLabel(status)}</Tag>)}
            {shipmentStatusFilter.map(status => <Tag key={`primary-shipment-${status}`} closable onClose={() => setShipmentStatusFilter(prev => prev.filter(item => item !== status))}>{status}</Tag>)}
            {holdReasonFilter.trim() && <Tag closable onClose={() => setHoldReasonFilter('')}>{holdReasonFilter}</Tag>}
            {stockIssuesFilter.map(issue => <Tag key={`primary-stock-${issue}`} closable onClose={() => setStockIssuesFilter(prev => prev.filter(item => item !== issue))}>{issue}</Tag>)}
          </div>
        )}
      </section>

      {/* Main Content Area */}
      <Row gutter={[16, 16]}>
        {/* Main Table Area - Full Width */}
        <Col xs={24}>
          <Card
            className="orders-table-card"
            style={{ 
              border: 'none',
              boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03), 0 1px 6px -1px rgba(0,0,0,0.02), 0 2px 4px 0 rgba(0,0,0,0.02)',
              height: '100%'
            }}
            bodyStyle={{ padding: 0 }}
          >
            <div className="orders-section-toolbar">
              <div className="orders-section-title">
                <Text strong style={{ fontSize: '14px' }}>Orders Found ({ordersWithPastOrder.length})</Text>
                <Text type="secondary" style={{ fontSize: '12px' }}>Updated {moment().format('hh:mm A')}</Text>
              </div>
            </div>

            <Table
              columns={columns}
              dataSource={ordersWithPastOrder}
              rowSelection={rowSelection}
              rowKey="orderID"
              size="small"
              loading={isRefreshing}
              sticky
              scroll={{ x: 1200 }}
              pagination={{
                pageSize: pageSize,
                showSizeChanger: true,
                onShowSizeChange: (current, size) => {
                  setPageSize(size);
                },
                showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} orders`,
                showQuickJumper: true,
                size: 'small',
                simple: screens.xs
              }}
              rowClassName={(record) => {
                if (pastOrderResult && record.orderID === pastOrderResult.orderID) return 'past-order-highlight-row';
                if (record.packed_status === 'Hold') return 'hold-row';
                if (record.packed_status === 'Completed') return 'completed-row';
                // Highlight if pending for more than 48 hours
                if (record.createdAt && moment().diff(moment(record.createdAt), 'hours') > 48) {
                  return 'delayed-row';
                }
                return '';
              }}
              onRow={(record) => ({
                onDoubleClick: (event) => {
                  const isInteractiveTarget = event.target.closest(
                    'button, input, textarea, select, a, .ant-checkbox-wrapper, .orders-copy-icon, .orders-inline-icon'
                  );

                  if (isInteractiveTarget) {
                    return;
                  }

                  setSelectedOrder(record);
                  setOrderDetailsVisible(true);
                }
              })}
              style={{ 
                borderRadius: 0,   
                border: 'none'
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* Order Details Drawer */}
      <Drawer
        placement="right"
        open={orderDetailsVisible}
        onClose={() => setOrderDetailsVisible(false)}
        width={screens.xs ? '100%' : 460}
        className="order-details-drawer"
        title={selectedOrder && (
          <div className="order-details-heading">
            <div className="order-details-heading-main">
              <Text className="order-details-kicker">ORDER DETAILS</Text>
              <div className="order-details-orderline">
                {(() => {
                  const courierMeta = getCourierMeta(selectedOrder.shipments?.[0]?.courier_name);
                  return courierMeta.image ? (
                    <img src={courierMeta.image} alt={courierMeta.label} className="order-details-courier-icon" />
                  ) : (
                    <div className="order-details-courier-icon fallback" aria-hidden="true"><TruckOutlined /></div>
                  );
                })()}
                <Title level={4}>{selectedOrder.orderID}</Title>
              </div>
            </div>
            <div className="order-details-meta">
              {(() => {
                const statusConfig = getDrawerStatusConfig(selectedOrder.packed_status);
                return (
                  <Tag className="order-details-status" color={statusConfig.color} icon={statusConfig.icon}>
                    {statusConfig.label}
                  </Tag>
                );
              })()}
              <Text type="secondary">{moment(selectedOrder.order_date).format('DD MMM YYYY, hh:mm A')}</Text>
            </div>
          </div>
        )}
        styles={{ body: { padding: 0 } }}
      >
        {selectedOrder && (
          <Tabs
            className="order-details-tabs"
            activeKey={orderDetailsTab}
            onChange={setOrderDetailsTab}
            items={[
              {
                key: 'overview',
                label: 'Overview',
                children: (
                  <div className="order-details-content">
                    <div className="order-details-grid">
                      <Card size="small" title="Customer Information" className="order-details-card compact-card">
                        <div className="detail-line">
                          <UserOutlined />
                          <Text strong>{selectedOrder.customer?.name || 'N/A'}</Text>
                        </div>
                        <div className="detail-line">
                          <PhoneOutlined />
                          <Text type="secondary">{selectedOrder.customer?.mobile || 'N/A'}</Text>
                        </div>
                        <div className="detail-line">
                          <MailOutlined />
                          <Text type="secondary" ellipsis>{selectedOrder.customer?.email || 'N/A'}</Text>
                        </div>
                        <Button
                          type="default"
                          size="small"
                          className="odoo-link-button"
                          icon={<span className="odoo-brand-mark">O</span>}
                          onClick={() => handleOpenOdoo(selectedOrder)}
                        >
                          View in Odoo
                        </Button>
                      </Card>

                      <Card size="small" title="Shipping Information" className="order-details-card compact-card">
                        <div className="courier-inline">
                          {(() => {
                            const courierMeta = getCourierMeta(selectedOrder.shipments?.[0]?.courier_name);
                            return courierMeta.image ? (
                              <img src={courierMeta.image} alt={courierMeta.label} className="order-details-courier-logo" />
                            ) : (
                              <div className="order-details-courier-logo fallback" aria-hidden="true"><TruckOutlined /></div>
                            );
                          })()}
                          <Text strong>{getCourierLabel(selectedOrder.shipments?.[0]?.courier_name)}</Text>
                        </div>
                        <div className="detail-line detail-line-spread">
                          <Text type="secondary">AWB</Text>
                          <Text strong>{selectedOrder.shipments?.[0]?.awb_code || 'N/A'}</Text>
                        </div>
                        <div className="detail-line detail-line-spread">
                          <Text type="secondary">Tracking</Text>
                          {selectedOrder.shipments?.[0]?.tracking_url || selectedOrder.shipments?.[0]?.carrier_tracking_url ? (
                            <Button type="link" size="small" className="tracking-button" onClick={() => window.open(selectedOrder.shipments[0].tracking_url || selectedOrder.shipments[0].carrier_tracking_url, '_blank', 'noopener,noreferrer')}>
                              Track Shipment
                            </Button>
                          ) : (
                            <Text type="secondary">Not available</Text>
                          )}
                        </div>
                      </Card>

                      <Card size="small" title="Package Information" className="order-details-card compact-card">
                        <div className="detail-line package-detail-line">
                          <Text type="secondary">Box Type</Text>
                          <Text strong>{selectedOrder.box_type || 'N/A'}</Text>
                        </div>
                        <div className="detail-line package-detail-line">
                          <Text type="secondary">Final Package Weight</Text>
                          <Text strong>
                            {Number.isFinite(Number(selectedOrder.final_package_weight))
                              ? `${Number(selectedOrder.final_package_weight).toFixed(2)} kg`
                              : 'N/A'}
                          </Text>
                        </div>
                      </Card>
                    </div>

                    <div className="compact-summary-grid">
                      <Card size="small" title="Pickup Status" className="order-details-card compact-card summary-card">
                        <div className="mini-stat">
                          <span className="mini-stat-icon"><TruckOutlined /></span>
                          <div>
                            <Text strong>{getPickupStatus(selectedOrder) || 'N/A'}</Text>
                            <div className="mini-stat-subtext">{getPickupStatus(selectedOrder) === 'Pickup Scheduled' ? 'Scheduled for pickup' : 'N/A'}</div>
                          </div>
                        </div>
                      </Card>

                      <Card size="small" title="Warehouse" className="order-details-card compact-card summary-card">
                        <Tag color={selectedOrder.warehouse_out === 'Yes' || selectedOrder.warehouse_out_date ? 'success' : 'default'} className="warehouse-badge">
                          {selectedOrder.warehouse_out === 'Yes' || selectedOrder.warehouse_out_date ? 'Completed' : 'Pending'}
                        </Tag>
                        {(selectedOrder.warehouse_out_by || selectedOrder.warehouse_out_date) && (
                          <div className="warehouse-out-meta">
                            <div>
                              <UserOutlined />
                              <Text type="secondary">Sent by:</Text>
                              <Text strong className="warehouse-out-name">{selectedOrder.warehouse_out_by || 'N/A'}</Text>
                            </div>
                            {selectedOrder.warehouse_out_date && (
                              <Text type="secondary">
                                {moment(selectedOrder.warehouse_out_date).format('DD MMM, hh:mm A')}
                              </Text>
                            )}
                          </div>
                        )}
                      </Card>
                    </div>

                    <Card size="small" title="Packing Progress" className="order-details-card compact-card">
                      <div className="packing-progress-list">
                        {getPackingProgressStages(selectedOrder).map((stage, index) => (
                          <div key={stage.key} className={`packing-stage ${stage.done ? 'done' : 'pending'}`}>
                            <div className="packing-stage-rail" aria-hidden="true">
                              {index < getPackingProgressStages(selectedOrder).length - 1 && <span className="packing-stage-line" />}
                            </div>
                            <div className="packing-stage-content">
                              <div className="packing-stage-title-row">
                                <span className={`packing-stage-dot ${stage.done ? 'done' : ''}`} />
                                <Text strong>{stage.label}</Text>
                              </div>
                              <Text type="secondary" className="packing-stage-time">{stage.time || 'Pending'}</Text>
                            </div>
                          </div>
                        ))}
                      </div>
                    </Card>

                    <div className="compact-summary-grid">
                      <Card size="small" title="Packed By" className="order-details-card compact-card summary-card">
                        <div className="mini-stat compact-column">
                          <span className="mini-stat-icon"><UserOutlined /></span>
                          <div>
                            <Text strong>{selectedOrder.packed_person_name || 'Unknown'}</Text>
                            <div className="mini-stat-subtext">{selectedOrder.packed_date ? moment(selectedOrder.packed_date).format('DD MMM, hh:mm A') : 'N/A'}</div>
                          </div>
                        </div>
                      </Card>

                      <Card size="small" title="Packing Status" className="order-details-card compact-card summary-card">
                        {getPackingStatus(selectedOrder.packed_status)}
                      </Card>
                    </div>

                    <Card size="small" title={`Products (${selectedOrder.products?.length || 0})`} className="order-details-card compact-card">
                      <div className="order-mini-products">
                        {(selectedOrder.products || []).slice(0, 3).map((product, index) => {
                          const image = getProductThumbnail(product);
                          return (
                            <div key={`${product?.name || 'item'}-${index}`} className="order-mini-product-row">
                              <div className="order-mini-product-image-wrap">
                                {image ? (
                                  <img src={image} alt={product?.name || 'Product'} className="order-mini-product-image" />
                                ) : (
                                  <div className="order-mini-product-image fallback"><BoxPlotOutlined /></div>
                                )}
                              </div>
                              <div className="order-mini-product-meta">
                                <Text strong>{product?.name || 'N/A'}</Text>
                                <Text type="secondary">{product?.sku || 'SKU N/A'}</Text>
                              </div>
                              <Text className="order-mini-product-qty">Qty {product?.quantity || 1}</Text>
                            </div>
                          );
                        })}
                      </div>
                      <Button type="link" className="compact-view-all" onClick={() => setOrderDetailsTab('products')}>
                        View all products →
                      </Button>
                    </Card>

                    {!!(selectedOrder.hold_reason || selectedOrder.stock_issues?.length) && (
                      <Alert
                        className="drawer-alert"
                        type={selectedOrder.stock_issues?.length ? 'error' : 'warning'}
                        showIcon
                        message={selectedOrder.hold_reason || 'Stock issues detected'}
                        description={selectedOrder.stock_issues?.join(', ') || 'Review the order details.'}
                      />
                    )}
                  </div>
                )
              },
              {
                key: 'packing',
                label: 'Packing',
                children: (
                  <div className="order-details-content packing-history-content">
                    <Card size="small" title="Current Packing Session" className="order-details-card compact-card">
                      <div className="packing-session-summary">
                        <Tag color={selectedOrder.packingSessionOpen ? 'processing' : 'default'}>
                          {selectedOrder.packingSessionOpen ? 'OPEN' : 'NOT OPEN'}
                        </Tag>
                        <Text strong>{selectedOrder.packingSessionId || 'No current session ID'}</Text>
                      </div>
                      <div className="detail-line detail-line-spread">
                        <Text type="secondary">Opened by</Text>
                        <Text strong>{selectedOrder.packingSessionOpenedByName || 'N/A'}</Text>
                      </div>
                      <div className="detail-line detail-line-spread">
                        <Text type="secondary">Opened at</Text>
                        <Text>{selectedOrder.packingSessionOpenedAt ? moment(selectedOrder.packingSessionOpenedAt).format('DD MMM YYYY, hh:mm A') : 'N/A'}</Text>
                      </div>
                      <div className="detail-line detail-line-spread">
                        <Text type="secondary">Closed by</Text>
                        <Text strong>{selectedOrder.packingSessionClosedByName || 'N/A'}</Text>
                      </div>
                      <div className="detail-line detail-line-spread">
                        <Text type="secondary">Closed at</Text>
                        <Text>{selectedOrder.packingSessionClosedAt ? moment(selectedOrder.packingSessionClosedAt).format('DD MMM YYYY, hh:mm A') : 'N/A'}</Text>
                      </div>
                    </Card>

                    <Card
                      size="small"
                      title={`Session History (${getPackingSessionHistory(selectedOrder).length})`}
                      className="order-details-card compact-card"
                    >
                      <div className="packing-record-list">
                        {getPackingSessionHistory(selectedOrder).length ? getPackingSessionHistory(selectedOrder).map((session, index) => (
                          <div className="packing-record" key={`${session.sessionId || 'session'}-${index}`}>
                            <div className="packing-record-heading">
                              <Text code>{session.sessionId || 'Session ID unavailable'}</Text>
                              <Tag color={session.status === 'OPEN' ? 'processing' : session.status === 'COMPLETED' ? 'success' : 'default'}>{session.status || 'CLOSED'}</Tag>
                            </div>
                            <div className="packing-record-line"><span>Opened by</span><strong>{session.openedByName || 'N/A'}</strong></div>
                            <div className="packing-record-line"><span>Opened at</span><strong>{session.openedAt ? moment(session.openedAt).format('DD MMM YYYY, hh:mm A') : 'N/A'}</strong></div>
                            <div className="packing-record-line"><span>Closed by</span><strong>{session.closedByName || 'N/A'}</strong></div>
                            <div className="packing-record-line"><span>Closed at</span><strong>{session.closedAt ? moment(session.closedAt).format('DD MMM YYYY, hh:mm A') : 'N/A'}</strong></div>
                            {!!session.reason && <div className="packing-record-line"><span>Reason</span><strong>{session.reason}</strong></div>}
                          </div>
                        )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No packing sessions recorded" />}
                      </div>
                    </Card>

                    <Card
                      size="small"
                      title={`Box Transactions (${packingMovements.length})`}
                      className="order-details-card compact-card"
                    >
                      <div className="packing-record-list">
                        {packingMovementsLoading ? (
                          <Text type="secondary">Loading box transactions...</Text>
                        ) : packingMovements.length ? packingMovements.map((movement) => (
                          <div className="packing-record" key={movement._id}>
                            <div className="packing-record-heading">
                              <strong>{movement.materialId?.materialName || 'Unknown material'}</strong>
                              <Tag color={movement.transactionType === 'RETURN' ? 'green' : movement.transactionType === 'DEDUCTION' ? 'orange' : 'default'}>{movement.transactionType || 'N/A'}</Tag>
                            </div>
                            <div className="packing-record-line"><span>Barcode</span><strong>{movement.barcode || 'N/A'}</strong></div>
                            <div className="packing-record-line"><span>Batch</span><strong>{movement.batchId?.lotNumber || 'N/A'}</strong></div>
                            <div className="packing-record-line"><span>Quantity</span><strong>{movement.quantity ?? 'N/A'}</strong></div>
                            <div className="packing-record-line"><span>Source</span><strong>{movement.source || 'N/A'}</strong></div>
                            <div className="packing-record-line"><span>Operator</span><strong>{movement.createdBy?.name || 'N/A'}</strong></div>
                            <div className="packing-record-line"><span>Date</span><strong>{movement.transactionDate ? moment(movement.transactionDate).format('DD MMM YYYY, hh:mm A') : 'N/A'}</strong></div>
                            <div className="packing-record-line"><span>Session</span><strong>{movement.packingSessionId || 'N/A'}</strong></div>
                            <div className="packing-record-line"><span>Reason</span><strong>{movement.reason || 'N/A'}</strong></div>
                          </div>
                        )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No box transactions recorded for this order" />}
                      </div>
                    </Card>
                  </div>
                )
              },
              {
                key: 'products',
                label: `Products (${selectedOrder.products?.length || 0})`,
                children: (
                  <div className="order-details-content products-tab-content">
                    {(selectedOrder.products || []).map((product, index) => {
                      const image = getProductThumbnail(product);
                      return (
                        <div key={`${product?.name || 'product'}-${index}`} className="product-card-row">
                          <div className="product-card-thumb-wrap">
                            {image ? (
                              <img src={image} alt={product?.name || 'Product'} className="product-card-thumb" />
                            ) : (
                              <div className="product-card-thumb fallback"><BoxPlotOutlined /></div>
                            )}
                          </div>
                          <div className="product-card-copy">
                            <Text strong>{product?.name || 'N/A'}</Text>
                            <Text type="secondary">SKU: {product?.sku || 'N/A'}</Text>
                            <Text type="secondary">Qty: {product?.quantity || 1}</Text>
                            {product?.updated_id && <Text type="secondary">Barcode: {product.updated_id}</Text>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              },
              {
                key: 'timeline',
                label: 'Timeline',
                children: (
                  <div className="order-details-content">
                    <Steps
                      direction="horizontal"
                      size="small"
                      current={(() => {
                        if (selectedOrder.shipments?.[0]?.status?.toUpperCase() === 'DELIVERED') return 3;
                        if (selectedOrder.warehouse_out === 'Yes' || selectedOrder.warehouse_out_date) return 2;
                        if (selectedOrder.packed_status === 'Completed') return 1;
                        return 0;
                      })()}
                      items={[
                        { title: 'Placed', description: selectedOrder.order_date ? moment(selectedOrder.order_date).format('DD MMM YYYY, hh:mm A') : 'N/A' },
                        { title: 'Packed', description: selectedOrder.packed_date ? moment(selectedOrder.packed_date).format('DD MMM YYYY, hh:mm A') : 'Pending' },
                        { title: 'Shipped', description: selectedOrder.warehouse_out_date ? moment(selectedOrder.warehouse_out_date).format('DD MMM YYYY, hh:mm A') : 'Pending' },
                        { title: 'Delivered', description: selectedOrder.shipments?.[0]?.status?.toUpperCase() === 'DELIVERED' ? 'Completed' : 'Pending' }
                      ]}
                    />
                  </div>
                )
              },
              {
                key: 'logs',
                label: 'Logs',
                children: (
                  <AntTable
                    className="order-logs-table"
                    size="small"
                    pagination={false}
                    rowKey={(_, index) => `log-${index}`}
                    dataSource={selectedOrder.activity_logs || selectedOrder.logs || selectedOrder.activities || []}
                    locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No activity logs available" /> }}
                    columns={[
                      { title: 'TIMESTAMP', key: 'timestamp', render: log => (log.createdAt || log.timestamp) ? moment(log.createdAt || log.timestamp).format('DD MMM, hh:mm A') : 'N/A' },
                      { title: 'USER / SYSTEM', key: 'actor', render: log => log.user || log.username || log.actor || log.system || 'System' },
                      { title: 'ACTION', key: 'action', render: log => log.action || log.message || log.event || 'N/A' },
                      { title: 'STATUS', key: 'status', render: log => log.status || 'N/A' }
                    ]}
                  />
                )
              }
            ]}
          />
        )}
      </Drawer>

      {/* Edit Drawer */}
      <Drawer
        title={
          <Space>
            <EditOutlined />
            <Text strong>Edit Order</Text>
          </Space>
        }
        placement="right"
        onClose={() => setEditModalVisible(false)}
        open={editModalVisible}
        width={450}
        className="orders-page-drawer"
        styles={{
          body: { padding: '24px' }
        }}
      >
        {editingOrder && (
          <Space direction="vertical" style={{ width: '100%' }} size={24}>
            {/* Order Summary */}
            <Card size="small">
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                <Text strong style={{ fontSize: '16px' }}>{editingOrder.orderID}</Text>
                <Space direction="vertical" size={2}>
                  <Space>
                    <UserOutlined style={{ color: '#666' }} />
                    <Text>{editingOrder.customer?.name}</Text>
                  </Space>
                  <Space>
                    <PhoneOutlined style={{ color: '#666' }} />
                    <Text>{editingOrder.customer?.mobile}</Text>
                  </Space>
                  <Space>
                    <MailOutlined style={{ color: '#666' }} />
                    <Text>{editingOrder.customer?.email}</Text>
                  </Space>
                </Space>
                <Divider style={{ margin: '12px 0' }} />
                <Text type="secondary">
                  Ordered: {moment(editingOrder.order_date).format('DD MMM YYYY, hh:mm A')}
                </Text>
                {editingOrder.packed_date && (
                  <Text type="secondary">
                    Packed: {moment(editingOrder.packed_date).format('DD MMM YYYY')}
                  </Text>
                )}
              </Space>
            </Card>

            {/* Edit Form */}
            <div>
              <Text strong style={{ display: 'block', marginBottom: 16, fontSize: '14px' }}>
                Update Shipping Details
              </Text>
              
              <Space direction="vertical" style={{ width: '100%' }} size={16}>
                <div>
                  <Text style={{ display: 'block', marginBottom: 8, fontSize: '13px', fontWeight: 500 }}>
                    Courier Name
                  </Text>
                  <Select
                    style={{ width: '100%' }}
                    value={isCourierOther ? 'Other' : (editFields.courier_name || undefined)}
                    onChange={(val) => {
                      if (val === 'Other') {
                        setIsCourierOther(true);
                        setEditFields({ ...editFields, courier_name: '' });
                      } else {
                        setIsCourierOther(false);
                        setEditFields({ ...editFields, courier_name: val });
                      }
                    }}
                    placeholder="Select courier"
                    size="middle"
                    allowClear
                  >
                    {COURIER_OPTIONS.map(c => <Option key={c} value={c}>{c}</Option>)}
                    <Option value="Other">Other (enter manually)</Option>
                  </Select>
                  {isCourierOther && (
                    <Input
                      value={editFields.courier_name}
                      onChange={e => setEditFields({...editFields, courier_name: e.target.value})}
                      placeholder="Enter courier name"
                      size="middle"
                      style={{ marginTop: 8 }}
                    />
                  )}
                </div>

                <div>
                  <Text style={{ display: 'block', marginBottom: 8, fontSize: '13px', fontWeight: 500 }}>
                    AWB Code
                  </Text>
                  <Input
                    value={editFields.awb_code}
                    onChange={e => setEditFields({...editFields, awb_code: e.target.value})}
                    placeholder="Enter AWB code"
                    size="middle"
                  />
                </div>

                <div>
                  <Text style={{ display: 'block', marginBottom: 8, fontSize: '13px', fontWeight: 500 }}>
                    Packing Status
                  </Text>
                  <Select
                    style={{ width: '100%' }}
                    value={editFields.packed_status}
                    onChange={value => setEditFields({...editFields, packed_status: value})}
                    placeholder="Select packing status"
                    size="middle"
                  >                                  
                    <Option value="Hold">On Hold</Option>
                    <Option value="Not Completed">Not Completed</Option>
                    <Option value="Cancelled">Cancelled</Option>
                    <Option value="Completed">Completed</Option>       
                    <Option value="Pending">Pending</Option>             
                  </Select>
                </div>

                <div>
                  <Text style={{ display: 'block', marginBottom: 8, fontSize: '13px', fontWeight: 500 }}>
                    Hold/Stock Note
                  </Text>
                  <Select
                    style={{ width: '100%', marginBottom: isOtherSelected ? 8 : 0 }}
                    value={isOtherSelected ? "Other" : (editFields.hold_reason || undefined)}
                    onChange={(val) => {
                      if (val === "Other") {
                        setIsOtherSelected(true);
                        setEditFields({ ...editFields, hold_reason: "" });
                      } else {
                        setIsOtherSelected(false);
                        setEditFields({ ...editFields, hold_reason: val });
                      }
                    }}
                    placeholder="Select reason"
                    allowClear
                  >
                    {HOLD_REASONS.map(r => <Option key={r} value={r}>{r}</Option>)}
                    <Option value="Other">Other</Option>
                  </Select>
                  {isOtherSelected && (
                    <Input.TextArea
                      value={editFields.hold_reason}
                      onChange={e => setEditFields({...editFields, hold_reason: e.target.value})}
                      placeholder="Enter custom reason..."
                      rows={2}
                    />
                  )}
                </div>

                <div>
                  <Text style={{ display: 'block', marginBottom: 8, fontSize: '13px', fontWeight: 500 }}>
                    Products with Stock Issues
                  </Text>
                  <Select
                    mode="multiple"
                    style={{ width: '100%' }}
                    value={editFields.stock_issues}
                    onChange={value => setEditFields({...editFields, stock_issues: value})}
                    placeholder="Select products..."
                  >
                    {editingOrder.products?.map(p => (
                      <Option key={p.name} value={p.name}>{p.name}</Option>
                    ))}
                  </Select>
                </div>
              </Space>
            </div>

            <Divider />

            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => setEditModalVisible(false)}>
                Cancel
              </Button>
              <Button type="primary" onClick={handleSaveEdit}>
                Save Changes
              </Button>
            </Space>
          </Space>
        )}
      </Drawer>

      {/* Filters Drawer */}
      <Drawer
        title={
          <Space>
            <FilterOutlined />
            <Text strong>Filters</Text>
          </Space>
        }
        placement="right"
        onClose={() => setFilterDrawerVisible(false)}
        open={filterDrawerVisible}
        width={screens.xs ? '100%' : 380}
        className="filters-drawer"
        footer={
          <div className="filters-drawer-footer">
            <Button onClick={clearAllFilters}>Clear All</Button>
            <Button type="primary" onClick={() => setFilterDrawerVisible(false)}>Apply Filters</Button>
          </div>
        }
      >
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          {/* Date Range */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '13px' }}>
              <CalendarOutlined style={{ marginRight: 8 }} />
              Packing Scheduled Date
            </Text>
            <div className="p-fluid">
              <Calendar
                value={dateRange}
                onChange={handleDateChange}
                selectionMode="range"
                readOnlyInput
                placeholder="Select Date Range"
                style={{ width: '100%' }}
                className="p-inputtext-sm"
                showIcon
                dateFormat="dd/mm/yy"
              />
            </div>
          </div>

          {/* Packed Date Range */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '13px' }}>
              <CalendarOutlined style={{ marginRight: 8 }} />
              Packed Date
            </Text>
            <div className="p-fluid">
              <Calendar
                value={packedDateRange}
                onChange={(e) => setPackedDateRange(e.value)}
                selectionMode="range"
                readOnlyInput
                placeholder="Select Packed Date Range"
                style={{ width: '100%' }}
                className="p-inputtext-sm"
                showIcon
                dateFormat="dd/mm/yy"
              />
            </div>
          </div>

          {/* AWB Status */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '13px' }}>
              <BarcodeOutlined style={{ marginRight: 8 }} />
              AWB Status
            </Text>
            <Select
              allowClear
              value={awbStatusFilter || undefined}
              onChange={setAwbStatusFilter}
              placeholder="All AWB statuses"
              style={{ width: '100%' }}
              options={[
                { label: 'With AWB', value: 'With AWB' },
                { label: 'Without AWB', value: 'Without AWB' }
              ]}
            />
          </div>

          {/* Courier Filter */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '13px' }}>
              <TruckOutlined style={{ marginRight: 8 }} />
              Courier
            </Text>
            <Select
              mode="multiple"
              placeholder="All Couriers"
              style={{ width: '100%' }}
              value={selectedCouriers}
              onChange={setSelectedCouriers}
              options={uniqueCouriers.map(courier => ({ label: courier, value: courier }))}
              size="middle"
              allowClear
              maxTagCount={2}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Space style={{ padding: '0 8px 4px' }}>
                    <Checkbox
                      checked={uniqueCouriers.length > 0 && selectedCouriers.length === uniqueCouriers.length}
                      indeterminate={selectedCouriers.length > 0 && selectedCouriers.length < uniqueCouriers.length}
                      onChange={(e) => setSelectedCouriers(e.target.checked ? uniqueCouriers : [])}
                    >
                      Select All
                    </Checkbox>
                  </Space>
                </>
              )}
            />
          </div>

          {/* Product Filter */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '13px' }}>
              <ShoppingOutlined style={{ marginRight: 8 }} />
              Product
            </Text>
            <Select
              mode="multiple"
              placeholder="All Products"
              style={{ width: '100%' }}
              value={selectedProducts}
              onChange={setSelectedProducts}
              options={uniqueProducts.map(product => ({ 
                label: product.sku ? `${product.name} (${product.sku})` : product.name, 
                value: product.name 
              }))}
              size="middle"
              allowClear
              maxTagCount={2}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Space style={{ padding: '0 8px 4px' }}>
                    <Checkbox
                      checked={uniqueProducts.length > 0 && selectedProducts.length === uniqueProducts.length}
                      indeterminate={selectedProducts.length > 0 && selectedProducts.length < uniqueProducts.length}
                      onChange={(e) => setSelectedProducts(e.target.checked ? uniqueProducts.map(p => p.name) : [])}
                    >
                      Select All
                    </Checkbox>
                  </Space>
                </>
              )}
            />
          </div>

          {/* Packing Status */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '13px' }}>
              <CheckCircleOutlined style={{ marginRight: 8 }} />
              Packing Status
            </Text>
            <Select
              mode="multiple"
              placeholder="All Status"
              style={{ width: '100%' }}
              value={packingStatusFilter}
              onChange={setPackingStatusFilter}
              options={uniquePackingStatus.map(status => ({ 
                label: getPackingStatusFilterLabel(status), 
                value: status,
                style: { 
                  color: status === 'Completed' ? '#52c41a' : 
                         status === 'Hold' ? '#fa541c' : 
                         status === 'Cancelled' ? '#1890ff' : '#666' 
                }
              }))}
              size="middle"
              allowClear
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Space style={{ padding: '0 8px 4px' }}>
                    <Checkbox
                      checked={uniquePackingStatus.length > 0 && packingStatusFilter.length === uniquePackingStatus.length}
                      indeterminate={packingStatusFilter.length > 0 && packingStatusFilter.length < uniquePackingStatus.length}
                      onChange={(e) => setPackingStatusFilter(e.target.checked ? uniquePackingStatus : [])}
                    >
                      Select All
                    </Checkbox>
                  </Space>
                </>
              )}
            />
          </div>

          {/* Hold Reason Filter */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '13px' }}>
              <WarningOutlined style={{ marginRight: 8 }} />
              Hold Reason
            </Text>
            <Select
              placeholder="Filter by reason"
              style={{ width: '100%' }}
              value={holdReasonFilter || undefined}
              onChange={setHoldReasonFilter}
              allowClear
              showSearch
            >
              {HOLD_REASONS.map(r => <Option key={r} value={r}>{r}</Option>)}
            </Select>
          </div>

          {/* Shipment Status */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '13px' }}>
              <GlobalOutlined style={{ marginRight: 8 }} />
              Shipment Status
            </Text>
            <Select
              mode="multiple"
              placeholder="All Status"
              style={{ width: '100%' }}
              value={shipmentStatusFilter}
              onChange={setShipmentStatusFilter}
              options={uniqueShipmentStatus.map(status => ({ label: status, value: status }))}
              size="middle"
              allowClear
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: '8px 0' }} />
                  <Space style={{ padding: '0 8px 4px' }}>
                    <Checkbox
                      checked={uniqueShipmentStatus.length > 0 && shipmentStatusFilter.length === uniqueShipmentStatus.length}
                      indeterminate={shipmentStatusFilter.length > 0 && shipmentStatusFilter.length < uniqueShipmentStatus.length}
                      onChange={(e) => setShipmentStatusFilter(e.target.checked ? uniqueShipmentStatus : [])}
                    >
                      Select All
                    </Checkbox>
                  </Space>
                </>
              )}
            />
          </div>

          {/* Stock Issues Filter */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '13px' }}>
              <InboxOutlined style={{ marginRight: 8 }} />
              Stock Issues
            </Text>
            <Select
              mode="multiple"
              placeholder="All Issues"
              style={{ width: '100%' }}
              value={stockIssuesFilter}
              onChange={setStockIssuesFilter}
              options={uniqueStockIssues.map(issue => ({ label: issue, value: issue }))}
              size="middle"
              allowClear
              maxTagCount={2}
            />
          </div>

          {/* Sort By */}
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '13px' }}>
              <SortAscendingOutlined style={{ marginRight: 8 }} />
              Sort By
            </Text>
            <Select
              style={{ width: '100%' }}
              value={sortConfig.field}
              onChange={field => handleSort(field)}
              size="middle"
            >
              <Option value="packingscheduledat">Packing Scheduled Date</Option>
              <Option value="order_date">Order Date</Option>
              <Option value="packed_status">Packing Status</Option>
              <Option value="customer.name">Customer Name</Option>
              <Option value="shipments.0.status">Shipment Status</Option>
            </Select>
            {sortConfig.field && (
              <Button 
                type="text" 
                size="small" 
                style={{ marginTop: 8 }}
                onClick={() => handleSort(sortConfig.field)}
              >
                {sortConfig.order === 'ascend' ? '↑ Ascending' : '↓ Descending'}
              </Button>
            )}
          </div>

        </Space>
      </Drawer>

      {/* Active Filters Badge (Floating) */}
      {false && (selectedCouriers.length > 0 || packingStatusFilter.length > 0 || shipmentStatusFilter.length > 0 || dateRange || awbStatusFilter || holdReasonFilter.trim() || stockIssuesFilter.length > 0) && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1000
        }}>
          <Card 
            size="small"
            className="active-filters-badge"
            style={{
              maxWidth: 300
            }}
            title={
              <Space>
                <FilterOutlined />
                <Text>Active Filters</Text>
              </Space>
            }
            extra={
              <Button 
                type="link" 
                size="small" 
                icon={<CloseOutlined />}
                onClick={clearAllFilters}
              />
            }
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {dateRange && dateRange[0] && dateRange[1] && (
                <Tag closable onClose={() => setDateRange(null)}>
                  📅 {moment(dateRange[0]).format('DD/MM')} - {moment(dateRange[1]).format('DD/MM')}
                </Tag>
              )}
              {packedDateRange && packedDateRange[0] && packedDateRange[1] && (
                <Tag closable onClose={() => setPackedDateRange(null)}>
                  📦 Packed: {moment(packedDateRange[0]).format('DD/MM')} - {moment(packedDateRange[1]).format('DD/MM')}
                </Tag>
              )}
              {selectedCouriers.map(courier => (
                <Tag key={courier} closable onClose={() => setSelectedCouriers(prev => prev.filter(c => c !== courier))}>
                  🚚 {courier}
                </Tag>
              ))}
              {packingStatusFilter.map(status => (
                <Tag key={status} closable onClose={() => setPackingStatusFilter(prev => prev.filter(s => s !== status))}>
                  📦 {getPackingStatusFilterLabel(status)}
                </Tag>
              ))}
              {shipmentStatusFilter.map(status => (
                <Tag key={status} closable onClose={() => setShipmentStatusFilter(prev => prev.filter(s => s !== status))}>
                  🌐 {status}
                </Tag>
              ))}
              {awbStatusFilter && (
                <Tag closable onClose={() => setAwbStatusFilter(null)}>
                  📄 {awbStatusFilter}
                </Tag>
              )}
              {holdReasonFilter.trim() && (
                <Tag closable onClose={() => setHoldReasonFilter('')}>
                  Reason: {holdReasonFilter}
                </Tag>
              )}
              {stockIssuesFilter.map(issue => (
                <Tag key={issue} closable onClose={() => setStockIssuesFilter(prev => prev.filter(i => i !== issue))}>
                  Issue: {issue}
                </Tag>
              ))}
            </Space>
          </Card>
        </div>
      )}

      {/* Bulk Actions Bar (Floating) */}
      {selectedRowKeys.length > 0 && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000
        }}>
          <Card 
            size="small"
            className="bulk-actions-bar"
            bodyStyle={{ padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '16px' }}
            style={{ boxShadow: '0 6px 16px rgba(0,0,0,0.12)', borderRadius: '8px', background: '#1f1f1f', color: 'white' }}
          >
            <Text style={{ color: 'white' }}>{selectedRowKeys.length} selected</Text>
            <Divider type="vertical" style={{ borderColor: 'rgba(255,255,255,0.3)' }} />
            <Button 
              type="primary" 
              size="small" 
              icon={<EditOutlined />}
              onClick={() => {
                setBulkStatus(null);
                setBulkHoldReason("");
                setIsBulkOtherSelected(false);
                setBulkUpdateModalVisible(true);
              }}
            >
              Update Status
            </Button>
            <Button 
              type="primary" 
              size="small" 
              icon={<SyncOutlined />}
              onClick={handleSyncSelectedOrders}
            >
              Sync Selected
            </Button>
            <Button 
              type="primary" 
              size="small" 
              icon={<DownloadOutlined />}
              onClick={() => handleExportExcel(filteredOrders.filter(o => selectedRowKeys.includes(o.orderID)))}
            >
              Export Selected
            </Button>
            <Button 
              type="text" 
              size="small" 
              icon={<CloseOutlined />}
              style={{ color: 'white' }}
              onClick={() => setSelectedRowKeys([])}
            />
          </Card>
        </div>
      )}

      {/* Bulk Update Modal */}
      <Modal
        title="Bulk Update Status"
        open={bulkUpdateModalVisible}
        onOk={handleBulkStatusUpdate}
        onCancel={() => setBulkUpdateModalVisible(false)}
        okText="Update Orders"
        okButtonProps={{ disabled: !bulkStatus }}
        width={500}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <Text>Select new status for {selectedRowKeys.length} selected orders:</Text>
          <Select
            style={{ width: '100%' }}
            placeholder="Select Status"
            onChange={setBulkStatus}
            value={bulkStatus}
          >
            <Option value="Not Completed">Not Completed</Option>
            <Option value="Hold">On Hold</Option>
            <Option value="Cancelled">Cancelled</Option>
          </Select>

          <Select
            style={{ width: '100%', marginBottom: isBulkOtherSelected ? 8 : 0 }}
            placeholder="Select Reason"
            value={isBulkOtherSelected ? "Other" : (bulkHoldReason || undefined)}
            onChange={(val) => {
              if (val === "Other") {
                setIsBulkOtherSelected(true);
                setBulkHoldReason("");
              } else {
                setIsBulkOtherSelected(false);
                setBulkHoldReason(val);
              }
            }}
            allowClear
          >
            {HOLD_REASONS.map(r => <Option key={r} value={r}>{r}</Option>)}
            <Option value="Other">Other</Option>
          </Select>

          {isBulkOtherSelected && (
            <Input.TextArea
              placeholder="Enter custom reason..."
              value={bulkHoldReason}
              onChange={e => setBulkHoldReason(e.target.value)}
              rows={2}
            />
          )}

          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="Select Products with Stock Issues (Optional)"
            value={bulkStockIssues}
            onChange={setBulkStockIssues}
          >
            {getUniqueProductsFromSelected().map(p => (
              <Option key={p} value={p}>{p}</Option>
            ))}
          </Select>
        </Space>
      </Modal>

      {/* Bulk AWB Update Modal */}
      <Modal
        title="Bulk AWB & Courier Update"
        open={bulkAwbModalVisible}
        onCancel={() => {
          if (!isBulkAwbUpdating) {
            setBulkAwbModalVisible(false);
            setBulkAwbData([]);
          }
        }}
        footer={[
          <Button key="cancel" onClick={() => setBulkAwbModalVisible(false)} disabled={isBulkAwbUpdating}>
            Cancel
          </Button>,
          <Button 
            key="update" 
            type="primary" 
            onClick={processBulkAwbUpdates} 
            disabled={bulkAwbData.length === 0}
            loading={isBulkAwbUpdating}
          >
            {isBulkAwbUpdating ? `Updating (${bulkAwbProgress}%)` : 'Update Orders'}
          </Button>
        ]}
      >
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <Alert
            message="Instructions"
            description="Download the sample template, fill in Order ID, Courier Name, and AWB Code, then upload to update."
            type="info"
            showIcon
          />
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Button icon={<DownloadOutlined />} onClick={downloadSampleAwbSheet}>
              Download Sample Sheet
            </Button>
            
            <Upload beforeUpload={handleAwbFileUpload} showUploadList={false} accept=".xlsx, .xls">
              <Button icon={<UploadOutlined />}>Upload Excel</Button>
            </Upload>
          </div>

          {bulkAwbData.length > 0 && <Alert message={`Ready to update ${bulkAwbData.length} orders`} type="success" showIcon />}
          {isBulkAwbUpdating && <Progress percent={bulkAwbProgress} status="active" />}
        </Space>
      </Modal>
    </div>
  );
};

export default OrdersPage;
